import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import * as Sentry from "@sentry/react";
import axios from "axios";
import { addDays,subDays } from "date-fns";
import i18next from "i18next";

import { FLEX_WORKER, FULL_TIME_WORKER, PART_TIME_WORKER } from "../../../assets/constants";
import { ACCEPTANCE_WINDOW_CLOSED_ERROR } from "../../../utils/acceptance-cutoff";
import {
  getContinuousWorkDayLimitMessage,
  getLanguage,
  getLanguageCode,
  getSelectedCompany,
  getWeeklyHoursLimitMessage,
  isFullTimeWorker,
  isPartTimeWorker,
  overwriteObjectsWithIds
} from "../../web-worker-app/util/helper";
import {
  ABSENT,
  EXCUSED_ABSENT,
  EXCUSED_TARDY,
  OVERTIME,
  PART_TIME,
  PRESENT,
  REGULAR,
  SETTINGS_LANGUAGE_PREFERENCE,
  TARDY
} from "../config/constants";
import { ConfirmedShiftData, Shift } from "../types";
import { addStartDateEndDate, overwriteShiftObjectsWithIds } from "../util/helper";
import i18n from "./../../../../i18n";

// const workerId = "911A9AF8-D82F-4749-919C-33D571A5AE02";

export const getPasscode = createAsyncThunk(
  "worker/login",
  /**  @param  {workerContact: string} */
  async ({ WorkerContact }: { WorkerContact: string }, thunkAPI) => {
    const language: any = localStorage.getItem("worker-language");
    const languagePreference = getLanguage(language);

    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Workers/GetPassCode`,
        {
          WorkerContact,
          languagePreference,
        }
      );

      if (response.status === 200) {
        return thunkAPI.fulfillWithValue({ WorkerContact });
      }
      return thunkAPI.rejectWithValue({
        ...response.data,
        WorkerContact,
      });
    } catch (err: any) {
      // console.log('thunkREJECT: ', err?.response, ' sdklfnsd: ', err)
      return thunkAPI.rejectWithValue({
        ...err.response?.data,
        status: err.response?.status,
        message: err.response?.data?.message,
        code: err.response?.data?.code,
        WorkerContact,
      });
    }
  }
);

export const verifyPasscode = createAsyncThunk(
  "worker/verify",
  /**  @param  {WorkerContact: string,passCode:string} */
  async (
    {
      WorkerContact,
      passCode,
    }: { WorkerContact: string; passCode: string },
    thunkAPI
  ) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const language: any = localStorage.getItem("worker-language");
      const languagePreference = getLanguage(language);

      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Workers/VerifyPassCode`,
        {
          WorkerContact,
          passCode,
          languagePreference,
          clientType: "web",
        }
      );

      if (response.status === 200) {
        return thunkAPI.fulfillWithValue({
          WorkerContact,
          passCode,
          ...response.data,
        });
      }
      return thunkAPI.rejectWithValue(response.data);
    } catch (err: any) {
      // console.log('thunkREJECT: ', err?.response, ' sdklfnsd: ', err)
      const errorResponse = err?.response?.data;
      errorResponse.status = err?.response?.status;
      return thunkAPI.rejectWithValue(errorResponse);
    }
  }
);

// autoLogin async

export const Refresh = createAsyncThunk(
  "worker/refresh",
  async (payload, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Workers/Refresh`,
        {
          refreshToken: localStorage.getItem("worker-refreshToken"),
          token: localStorage.getItem("worker-token"),
          clientType: "web",
        }
      );

      if (response.status === 200) {
        return thunkAPI.fulfillWithValue(response?.data);
      }
      return thunkAPI.rejectWithValue(response.data);
    } catch (err: any) {
      // console.log('thunkREJECT: ', err?.response, ' sdklfnsd: ', err)
      const errorResponse = err?.response?.data;
      errorResponse.status = err?.response?.status;
      return thunkAPI.rejectWithValue(errorResponse);
    }
  }
);

export const getConfirmedShifts = createAsyncThunk(
  "worker/getConfirmedShifts",
  /**
   * @param payload {startDate: string, endDate: string, updating: boolean}
   * @param thunkAPI
   */
  async (
    payload: {
      startDate: string;
      endDate: string;
      updating: boolean;
    },
    thunkAPI
  ) => {
    const data = {};
    try {
      const {startDate, endDate} = payload;
      // Add buffer days in start and end date
      const finalStartDate = subDays(new Date(startDate), 1).toISOString();
      const finalEndDate = addDays(new Date(endDate), 1).toISOString();
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/Workers/Shifts?startDate=${finalStartDate}&endDate=${finalEndDate}`,
        {
          data,
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );

      if (response.status === 200) {
        const allConfirmedShifts = response.data;

        const now = new Date();
        const startOfTodayLocal = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          0, 0, 0, 0
        ).getTime();

        const sD = new Date(startDate);
        const eD = new Date(endDate);

        const requestedStart = new Date(sD.getFullYear(), sD.getMonth(), sD.getDate(), 0, 0, 0, 0).getTime();
        const requestedEnd = new Date(eD.getFullYear(), eD.getMonth(), eD.getDate(), 23, 59, 59, 999).getTime();
        // Exclude shifts based on actual start and end date
        const filteredAllConfirmedShifts = allConfirmedShifts.filter((shift: any) => {
          const shiftStartTime = new Date(shift.flexiStartDateTime || shift.startDateTime).getTime();
          const shiftEndTime = new Date(shift.flexiEndDateTime || shift.endDateTime).getTime();

          return (
            (shiftStartTime >= requestedStart || shiftEndTime > requestedStart) &&
            shiftStartTime <= requestedEnd
          );
        });
        const allConfirmedShiftsWithDates =
          addStartDateEndDate(filteredAllConfirmedShifts);
        return thunkAPI.fulfillWithValue({
          shifts: allConfirmedShiftsWithDates,
          updating: payload.updating,
          endDateTime: payload.endDate,
        });
      } else if (response.status === 204) {
        return thunkAPI.fulfillWithValue({
          shifts: [],
          endDateTime: payload.endDate,
          updating: payload.updating,
        });
      } else
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-occurred"),
        });
    } catch (error: any) {
      Sentry.captureException(error);
      const errorResponse = error?.response?.data;
      errorResponse.status = error?.response?.status;
      if (error.message === "Request failed with status code 404")
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_no-data-found"),
        });
      else return thunkAPI.rejectWithValue(errorResponse);
    }
  }
);

export const getSkillLevels = createAsyncThunk(
  "worker/getSkillLevels",
  async (payload, thunkAPI) => {
    try {
      const data = {};
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/Skill/Levels`,
        {
          data,
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error: any) {
      const errorResponse = error?.response?.data;
      errorResponse.status = error?.response?.status;
      Sentry.captureException(error);
      if (error.message === "Request failed with status code 404")
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_no-data-found"),
        });
      else return thunkAPI.rejectWithValue(errorResponse);
    }
  }
);

export const fetchCancelShiftReasons = createAsyncThunk(
  "worker/fetchCancelShiftReasons",
  async (payload, thunkAPI) => {
    try {
      const data = null;
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/shift/cancelreasons/worker`,
        {
          data,
          headers: {
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response?.status === 200) {
        return thunkAPI.fulfillWithValue(response.data ?? []);
      } else if (response?.status === 204) {
        return thunkAPI.fulfillWithValue([]);
      } else
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-occurred"),
        });
    } catch (error: any) {
      Sentry.captureException(error);
      const errorResponse = error?.response?.data;
      errorResponse.status = error?.response?.status;
      errorResponse.message = i18next.t(
        "worker-slice_error-fetching-cancel-reason"
      );
      return thunkAPI.rejectWithValue(errorResponse);
    }
  }
);

export const fetchSpotsLeft = createAsyncThunk(
  "worker/getSpotsLeft",
  async (payload: any, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Shifts/SpotsLeft`,
        payload,
        {
          headers: {
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response?.status === 200) {
        return thunkAPI.fulfillWithValue(response.data ?? []);
      } else {
        return thunkAPI.fulfillWithValue([]);
      }
    } catch (error: any) {
      Sentry.captureException(error);
      const errorResponse = error?.response?.data;
      errorResponse.status = error?.response?.status;
      errorResponse.message = i18next.t(
        "worker-slice_error-fetching-spots-left"
      );
      return thunkAPI.rejectWithValue(errorResponse);
    }
  }
);

export const cancelShift = createAsyncThunk(
  "worker/cancelShift",
  /**  @param payload {{shiftId: number, status: string, startDateTime: string, endDateTime: string}}
   * @param thunkAPI
   */
  async (
    payload: {
      shiftId: number;
      status: string;
      startDateTime: string;
      endDateTime: string;
      cancelReasonId: number | null;
      OtherCancelReason: string | null;
    },
    thunkAPI
  ) => {
    try {
      const data = {
        shiftId: payload.shiftId,
        status: payload.status,
        cancelReasonId: payload.cancelReasonId,
        OtherCancelReason: payload.OtherCancelReason,
        forceCancel: false,
        source: "mobile",
      };
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Workers/Signup`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );

      if (!response) throw new Error("response not defined");
      else if (response.status === 200)
        if (Object.prototype.hasOwnProperty.call(response.data, "details")) {
          return thunkAPI.fulfillWithValue({
            ...response.data,
            startDateTime: payload.startDateTime,
            endDateTime: payload.endDateTime,
          });
        } else {
          return thunkAPI.fulfillWithValue(payload);
        }
      else
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-cancelling-shift"),
        });
    } catch (error: any) {
      Sentry.captureException(error);
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        const errorResponse = error.response.data;
        errorResponse.status = error?.response?.status;
        if (Object.prototype.hasOwnProperty.call(errorResponse, "message")) {
          let finalErrorResponse = errorResponse;
          // handle contact from error response
          if (Object.prototype.hasOwnProperty.call(errorResponse, "contact")) {
            if (errorResponse.contact) {
              const contactNumbers = errorResponse.contact.split(",");
              contactNumbers.forEach((contactNumber: string, index: number) => {
                if (index === 0) {
                  // Append contact to error response
                  finalErrorResponse = `${finalErrorResponse.trim()} +${contactNumber}`;
                } else {
                  finalErrorResponse = `${finalErrorResponse.trim()} or +${contactNumber}`;
                }
              });
            }
          }
          return thunkAPI.rejectWithValue(finalErrorResponse);
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-occurred"),
            status: error?.response?.status,
          });
        }
      } else {
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-cancelling-shift"),
        });
      }
    }
  }
);

export const forceCancelShift = createAsyncThunk(
  "worker/forceCancelShift",
  async (
    payload: {
      shiftId: number;
      startDateTime: string;
      endDateTime: string;
      cancelReasonId: number | null;
      OtherCancelReason: string | null;
    },
    thunkAPI
  ) => {
    try {
      const data = {
        shiftId: payload.shiftId,
        status: "C",
        forceCancel: true,
        cancelReasonId: payload.cancelReasonId,
        OtherCancelReason: payload.OtherCancelReason,
      };
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Workers/Signup`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response?.status === 200) {
        return thunkAPI.fulfillWithValue(payload);
      } else {
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-cancelling-shift"),
        });
      }
    } catch (error: any) {
      Sentry.captureException(error);
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        const errorResponse = error.response.data;
        errorResponse.status = error?.response?.status;
        if (Object.prototype.hasOwnProperty.call(errorResponse, "message")) {
          let finalErrorResponse = errorResponse;
          // handle contact from error response
          if (Object.prototype.hasOwnProperty.call(errorResponse, "contact")) {
            if (errorResponse.contact) {
              const contactNumbers = errorResponse.contact.split(",");
              contactNumbers.forEach((contactNumber: string, index: number) => {
                if (index === 0) {
                  // Append contact to error response
                  finalErrorResponse = `${finalErrorResponse.trim()} +${contactNumber}`;
                } else {
                  finalErrorResponse = `${finalErrorResponse.trim()} or +${contactNumber}`;
                }
              });
            }
          }
          return thunkAPI.rejectWithValue(finalErrorResponse);
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-occurred"),
            status: error?.response?.status,
          });
        }
      } else {
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-cancelling-shift"),
        });
      }
    }
  }
);

export const getUpcomingShifts = createAsyncThunk(
  "worker/getUpcomingShifts",
  /**
   * @param payload {startDate: string, endDate: string, updating: boolean}
   * @param thunkAPI
   */
  async (
    payload: {
      startDate: string;
      endDate: string;
      updating: boolean;
    },
    thunkAPI
  ) => {
    // const workerid = thunkAPI.getState().worker.workerId || payload;
    const data = {};
    try {
      const { startDate, endDate, updating } = payload as unknown as {
        startDate: string;
        endDate: string;
        updating: string;
      };
      // Add buffer days in start and end date
      const finalStartDate = subDays(new Date(startDate), 1).toISOString();
      const finalEndDate = addDays(new Date(endDate), 1).toISOString();

      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/Workers/UpcomingShifts?startDate=${finalStartDate}&endDate=${finalEndDate}`,
        {
          data,
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );

      if (response.status === 200) {
        const allUpcomingShifts = response.data;

        const now = new Date();
        const startOfTodayLocal = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          0, 0, 0, 0
        ).getTime();

        const sD = new Date(startDate);
        const eD = new Date(endDate);

        const requestedStart = new Date(sD.getFullYear(), sD.getMonth(), sD.getDate(), 0, 0, 0, 0).getTime();
        const requestedEnd = new Date(eD.getFullYear(), eD.getMonth(), eD.getDate(), 23, 59, 59, 999).getTime();
        const filteredAllUpcomingShifts = allUpcomingShifts.filter((shift: any) => {
          const shiftStartTime = new Date(shift.flexiStartDateTime || shift.startDateTime).getTime();
          const shiftEndTime = new Date(shift.flexiEndDateTime || shift.endDateTime).getTime();

          const startOfTodayRequested = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0).getTime();
          const isTodayRequested = requestedStart <= startOfTodayRequested && requestedEnd >= startOfTodayRequested;

          if (isTodayRequested) {
            // If today is requested, include shifts that end today or later
            return shiftEndTime >= startOfTodayRequested && shiftStartTime <= requestedEnd;
          }

          return (
            (shiftStartTime >= requestedStart || shiftEndTime > requestedStart) &&
            shiftStartTime <= requestedEnd
          );
        });
        const shiftIds = filteredAllUpcomingShifts
          .map((shift: { id: number }) => shift.id);
        let spotsLeftResponse: any = { status: 200, data: [] };
        if (shiftIds.length > 0) {
          spotsLeftResponse = await uninterceptedAxiosInstance.post(
            `${process.env.REACT_APP_API_END_POINT}/Shifts/SpotsLeft`,
            shiftIds,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: localStorage.getItem("worker-token"),
              },
            }
          );
        }
        if (filteredAllUpcomingShifts.length === 0) {
          return thunkAPI.fulfillWithValue({
            shifts: [],
            endDateTime: endDate,
            updating: updating,
          });
        }
        let allUpcomingShiftsWithDates = addStartDateEndDate(filteredAllUpcomingShifts);
        if (spotsLeftResponse.status === 200) {
          allUpcomingShiftsWithDates = allUpcomingShiftsWithDates.map(
            (shift: { id: number }) => {
              const spotsLeft = spotsLeftResponse.data.find(
                (spot: { shiftId: number; spotsLeft: number }) =>
                  spot.shiftId === shift.id
              );
              if (spotsLeft) {
                return {
                  ...shift,
                  spotsLeft: spotsLeft.spotsLeft,
                  slotsAvailability: spotsLeft?.slotsAvailability,
                };
              } else {
                return shift;
              }
            }
          );
        }
        return thunkAPI.fulfillWithValue({
          shifts: allUpcomingShiftsWithDates,
          endDateTime: endDate,
          updating: updating,
        });
      } else if (response.status === 204) {
        return thunkAPI.fulfillWithValue({
          shifts: [],
          endDateTime: endDate,
          updating: updating,
        });
      } else
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-occurred"),
        });
    } catch (error: any) {
      Sentry.captureException(error);
      // console.log(error);
      if (error.message === "Request failed with status code 404")
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_no-data-found"),
        });
      else
        return thunkAPI.rejectWithValue({
          error: error.response.data?.message,
          status: error?.response?.status,
        });
    }
  }
);

// A: Accept
export const confirmShift = createAsyncThunk(
  "worker/confirmShift",
  /**  @param payload {{shiftId: number, status: string, checkConsecutiveShiftSignup: boolean, startDateTime: string, endDateTime: string}}
   * @param thunkAPI
   */
  async (
    payload: {
      shiftId: number;
      status: string;
      checkConsecutiveShiftSignup: boolean;
      startDateTime: string;
      endDateTime: string;
      slot?: number;
      slotStart?: string;
      slotEnd?: string;
      flexiStartDateTime?: string;
      flexiEndDateTime?: string;
    },
    thunkAPI
  ) => {
    try {
      const {
        shiftId,
        status,
        checkConsecutiveShiftSignup,
        startDateTime,
        endDateTime,
        slot,
        slotStart,
        slotEnd,
      } = payload as unknown as {
        shiftId: number;
        status: string;
        checkConsecutiveShiftSignup: boolean;
        startDateTime: string;
        endDateTime: string;
        slot?: number;
        slotStart?: string;
        slotEnd?: string;
        flexiStartDateTime?: string;
        flexiEndDateTime?: string;
      };
      const data: ConfirmedShiftData = {
        // workerId: thunkAPI.getState().worker.workerId,
        shiftId: shiftId,
        status: status,
        checkConsecutiveShiftSignup: checkConsecutiveShiftSignup,
      };

      if (slot || slotStart || slotEnd) {
        data.slot = slot;
        data.slotStart = slotStart;
        data.slotEnd = slotEnd;
      }

      if (payload.flexiStartDateTime) {
        data.flexiStartDateTime = payload.flexiStartDateTime;
      }
      if (payload.flexiEndDateTime) {
        data.flexiEndDateTime = payload.flexiEndDateTime;
      }

      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Workers/Signup`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );

      // console.log(response);

      if (response.status === 200) return thunkAPI.fulfillWithValue(payload);
    } catch (error: any) {
      Sentry.captureException(error);
      const startDateTime = Object.prototype.hasOwnProperty.call(
        payload,
        "startDateTime"
      )
        ? payload.startDateTime
        : "";
      const endDateTime = Object.prototype.hasOwnProperty.call(
        payload,
        "endDateTime"
      )
        ? payload.endDateTime
        : "";

      if (error?.response?.status === 429) {
        return thunkAPI.rejectWithValue({
          status: error?.response?.status,
          message: i18n.t("worker-slice_error-confirming-shift"),
        });
      }
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        if (Object.prototype.hasOwnProperty.call(error?.response, "data")) {
          let errorResponse = error?.response?.data;
          errorResponse.status = error?.response?.status;
          errorResponse = {
            ...errorResponse,
            startDateTime,
            endDateTime,
          };
          if (Object.prototype.hasOwnProperty.call(errorResponse, "message")) {
            return thunkAPI.rejectWithValue({
              ...errorResponse,
              startDateTime,
              endDateTime,
            });
          } else {
            return thunkAPI.rejectWithValue({
              // Keep errorCode: the cutoff rejection carries no message.
              errorCode: errorResponse.errorCode,
              message: i18next.t("worker-slice_error-confirming-shift"),
              status: error?.response?.status,
              startDateTime,
              endDateTime,
            });
          }
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-confirming-shift"),
            status: error?.response?.status,
            startDateTime,
            endDateTime,
          });
        }
      } else if (Object.prototype.hasOwnProperty.call(error, "message")) {
        return thunkAPI.rejectWithValue({
          ...error,
          startDateTime,
          endDateTime,
        });
      } else {
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-confirming-shift"),
          startDateTime,
          endDateTime,
        });
      }
    }
  }
);

export const viewShift = createAsyncThunk(
  "worker/viewShift",
  async (payload: any, thunkAPI: any) => {
    try {
      const viewedShifts = thunkAPI.getState().worker.viewShift.viewedShifts;
      if (!viewedShifts.includes(payload)) {
        const data = {
          shiftId: payload,
        };
        const uninterceptedAxiosInstance = axios.create();
        const response = await uninterceptedAxiosInstance.post(
          `${process.env.REACT_APP_API_END_POINT}/Workers/ViewShift`,
          data,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: localStorage.getItem("worker-token"),
            },
          }
        );
        if (response.status === 200)
          return thunkAPI.fulfillWithValue([...viewedShifts, payload]);
      } else {
        return thunkAPI.fulfillWithValue(viewedShifts);
      }
    } catch (error: any) {
      Sentry.captureException(error);
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        const errorResponse = error.response.data;
        errorResponse.status = error.response.status;
        if (Object.prototype.hasOwnProperty.call(errorResponse, "message")) {
          return thunkAPI.rejectWithValue(errorResponse);
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-occurred"),
            status: error?.response?.status,
          });
        }
      } else if (error?.message === "Request failed with status code 404")
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_no-data-found"),
        });
      else {
        if (Object.prototype.hasOwnProperty.call(error, "message")) {
          return thunkAPI.rejectWithValue({ message: error.message });
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-occurred"),
          });
        }
      }
    }
  }
);

export const markShiftsStatus = createAsyncThunk(
  "worker/markShiftsStatus",
  async (payload: any, thunkAPI: any) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Workers/MarkShiftsStatus`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response.status === 200) {
        return thunkAPI.fulfillWithValue(payload);
      } else return thunkAPI.rejectWithValue(response.data);
    } catch (error: any) {
      Sentry.captureException(error);
      return thunkAPI.rejectWithValue(error.response);
    }
  }
);

export const declineShift = createAsyncThunk(
  "worker/declineShift",
  /**
   * @param payload {shiftId: number, startDateTime: string, endDateTime: string, slot?: number, slotStart?: string, slotEnd?: string}
   * @param thunkAPI
   */
  async (payload: {
    shiftId: number;
    startDateTime: string;
    endDateTime: string;
    slot?: number;
    slotStart?: string;
    slotEnd?: string;
    flexiStartDateTime?: string;
    flexiEndDateTime?: string;
  }, thunkAPI: any) => {
    try {
      const data: {
        shiftId: number;
        status: string;
        slot?: number;
        slotStart?: string;
        slotEnd?: string;
        flexiStartDateTime?: string;
        flexiEndDateTime?: string;
      } = {
        shiftId: payload.shiftId,
        status: "D",
      };
      if(payload.slot || payload.slotStart || payload.slotEnd) {
        data.slot = payload.slot;
        data.slotStart = payload.slotStart;
        data.slotEnd = payload.slotEnd;
      }
      if (payload.flexiStartDateTime) {
        data.flexiStartDateTime = payload.flexiStartDateTime;
      }
      if (payload.flexiEndDateTime) {
        data.flexiEndDateTime = payload.flexiEndDateTime;
      }
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Workers/Signup`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );

      // console.log(response);

      if (response.status === 200) return thunkAPI.fulfillWithValue(payload);
    } catch (error: any) {
      Sentry.captureException(error);
      return thunkAPI.rejectWithValue(error.response);
    }
  }
);

export const getProfile = createAsyncThunk(
  "worker/getProfile",
  async (payload, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/WorkerProfile/Details`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error: any) {
      Sentry.captureException(error);
      return thunkAPI.rejectWithValue(error.response);
    }
  }
);

export const getProfileSkills = createAsyncThunk(
  "worker/getProfileSkills",
  async (payload, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/WorkerProfile/Skills`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error: any) {
      Sentry.captureException(error);
      return thunkAPI.rejectWithValue(error.response);
    }
  }
);

export const getWorkHistory = createAsyncThunk(
  "worker/getWorkHistory",
  async (payload: { startDate: string; endDate: string }, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const { startDate, endDate } = payload;
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/workers/workhistory?startDate=${startDate}&endDate=${endDate}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      return thunkAPI.fulfillWithValue(response.data);
    } catch (error: any) {
      Sentry.captureException(error);
      return thunkAPI.rejectWithValue(error.response);
    }
  }
);

export const createSwapRequest = createAsyncThunk(
  "worker/createSwapRequest",
  async (
    payload: { shiftId: number; comment: string },
    thunkAPI
  ) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/ShiftSwap/Create`,
        { shiftId: payload.shiftId, comment: payload.comment },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response.status === 200) {
        return thunkAPI.fulfillWithValue({
          response: response.data,
          shiftId: payload.shiftId,
        });
      }
      return thunkAPI.rejectWithValue({
        message: i18next.t("offer-shift_shift-swap-error"),
      });
    } catch (error: any) {
      Sentry.captureException(error);
      return thunkAPI.rejectWithValue(
        error.response?.data ?? {
          message: i18next.t("offer-shift_shift-swap-error"),
        }
      );
    }
  }
);

export const getNotifications = createAsyncThunk(
  "worker/getNotifications",
  async (payload: { id: number; type: "new" | "old" } | undefined, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const params =
        payload && payload.id !== undefined && payload.type
          ? { id: payload.id, type: payload.type }
          : undefined;
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/Workers/Notifications`,
        {
          params,
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response.status === 200 && Array.isArray(response.data)) {
        return thunkAPI.fulfillWithValue(response.data);
      }
      return thunkAPI.rejectWithValue({
        message: i18next.t("worker-slice_error-unexpected-response"),
      });
    } catch (error: any) {
      Sentry.captureException(error);
      return thunkAPI.rejectWithValue(
        error.response?.data ?? {
          message: i18next.t("worker-slice_error-unexpected-response"),
        }
      );
    }
  }
);

export const getRegularShifts = createAsyncThunk(
  "worker/getRegularShifts",
  /**
   * @param payload {startDate: string, endDate: string, updating: boolean}
   * @param thunkAPI
   */
  async (
    payload: {
      startDate: string;
      endDate: string;
      updating: boolean;
    },
    thunkAPI
  ) => {
    try {
      const { startDate, endDate, updating } = payload as unknown as {
        startDate: string;
        endDate: string;
        updating: boolean;
      };
      // Add buffer days in start and end date
      const finalStartDate = subDays(new Date(startDate), 1).toISOString();
      const finalEndDate = addDays(new Date(endDate), 1).toISOString();
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/Workers/rShifts?startDate=${finalStartDate}&endDate=${finalEndDate}`,
        {
          headers: {
            "Content-type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );

      if (response?.status === 200) {
        const allRegularShifts = response?.data;
        const now = new Date();
        const startOfTodayLocal = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          0, 0, 0, 0
        ).getTime();

        const sD = new Date(startDate);
        const eD = new Date(endDate);

        const requestedStart = new Date(sD.getFullYear(), sD.getMonth(), sD.getDate(), 0, 0, 0, 0).getTime();
        const requestedEnd = new Date(eD.getFullYear(), eD.getMonth(), eD.getDate(), 23, 59, 59, 999).getTime();
        // Exclude shifts based on actual start and end date
        const filteredAllRegularShifts = allRegularShifts.filter((shift: any) => {
          const shiftStartTime = new Date(shift.shiftStartTime).getTime();
          const shiftEndTime = new Date(shift.shiftEndTime).getTime();

          return (
            (shiftStartTime >= requestedStart || shiftEndTime > requestedStart) &&
            shiftStartTime <= requestedEnd
          );
        });
        const allRegularShiftsWithDates = addStartDateEndDate(filteredAllRegularShifts);
        return thunkAPI.fulfillWithValue({
          shifts: allRegularShiftsWithDates,
          updating: updating,
          endDateTime: endDate,
        });
      } else if (response?.status === 204) {
        return thunkAPI.fulfillWithValue({
          shifts: [],
          updating: updating,
          endDateTime: endDate,
        });
      } else
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-occurred"),
        });
    } catch (error: any) {
      Sentry.captureException(error);
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        const errorResponse = error.response.data;
        errorResponse.status = error?.response?.status;
        if (Object.prototype.hasOwnProperty.call(errorResponse, "message")) {
          return thunkAPI.rejectWithValue(errorResponse);
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-occurred"),
            status: error?.response?.status,
          });
        }
      } else if (error?.message === "Request failed with status code 404")
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_no-data-found"),
        });
      else {
        if (Object.prototype.hasOwnProperty.call(error, "message")) {
          return thunkAPI.rejectWithValue({ message: error.message });
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-occurred"),
          });
        }
      }
    }
  }
);

export const getFTSchedulingStatuses = createAsyncThunk(
  "worker/getFTSchedulingStatuses",
  /**
   * @param payload {startDate: string, endDate: string, updating: boolean}
   * @param thunkAPI
   */
  async (
    payload,
    thunkAPI
  ) => {
    try {
      
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/Workers/GetSchedulingStatuses`,
        {
          headers: {
            "Content-type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );

      if (response?.status === 200) {
        return thunkAPI.fulfillWithValue(response.data);
      } else
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-occurred"),
        });
    } catch (error: any) {
      Sentry.captureException(error);
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        const errorResponse = error.response.data;
        errorResponse.status = error?.response?.status;
        if (Object.prototype.hasOwnProperty.call(errorResponse, "message")) {
          return thunkAPI.rejectWithValue(errorResponse);
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-occurred"),
            status: error?.response?.status,
          });
        }
      } else if (error?.message === "Request failed with status code 404")
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_no-data-found"),
        });
      else {
        if (Object.prototype.hasOwnProperty.call(error, "message")) {
          return thunkAPI.rejectWithValue({ message: error.message });
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-occurred"),
          });
        }
      }
    }
  }
);


export const getAcknowledgements = createAsyncThunk(
  "worker/acknowledgements",
  async (payload, { fulfillWithValue, rejectWithValue, getState }) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/Workers/Acknowledgements`,
        {
          headers: {
            "Content-type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response.status === 200) {
        return fulfillWithValue(response.data);
      } else
        return rejectWithValue({
          message: i18next.t("worker-slice_error-unexpected-response"),
          status: response?.status,
        });
    } catch (error: any) {
      Sentry.captureException(error);
      return rejectWithValue({
        message: i18next.t("worker-slice_error-unexpected-response"),
        status: error?.response?.status,
      });
    }
  }
);

export const acknowledgeShift = createAsyncThunk(
  "worker/acknowledgeShift",
  async (payload: any, { fulfillWithValue, rejectWithValue, getState }) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Workers/Acknowledge`,
        {
          shiftIds: [payload.id],
        },
        {
          headers: {
            "Content-type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response.status === 200) {
        return fulfillWithValue(payload);
      } else return rejectWithValue(response.data);
    } catch (error: any) {
      Sentry.captureException(error);
      return rejectWithValue(error.response);
    }
  }
);

export const getCustomLogo = createAsyncThunk(
  "worker/getCustomLogo",
  async (payload, { fulfillWithValue, rejectWithValue, getState }) => {
    try {
      const companyId = (getState() as any).worker.settings.companyId as string;
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/Companies/Branding?CompanyId=${companyId}`,
        {
          headers: {
            "Content-type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response.status === 200) {
        return fulfillWithValue(response.data);
      } else return rejectWithValue(response.data);
    } catch (error: any) {
      Sentry.captureException(error);
      return rejectWithValue(error.response);
    }
  }
);

export const updateLanguagePreference = (language: string) => {
  const languageCode = getLanguageCode(language);
  i18n.changeLanguage(languageCode);
  localStorage.setItem("worker-language", languageCode);
};

export const getSettings = createAsyncThunk(
  "worker/GetSettings",
  async (payload, thunkAPI) => {
    try {
      const workerToken = localStorage.getItem("worker-token");
      const uninterceptedAxiosInstance = axios.create();
      const language = localStorage.getItem("worker-language");
      const languagePreference = getLanguage(language);
      const response = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/Workers/GetSettings`,
        {
          headers: {
            "Content-type": "application/json",
            Authorization: workerToken,
          },
        }
      );
      if (response.status === 200) {
        if (Array.isArray(response.data) && response.data.length > 1) {
          const responseArray = response.data;
          const data = {
            smsNotifications: false,
            smsNotificationsId: 2,
            showSmsToggle: false,
            whatsappNotifications: false,
            whatsappNotificationsId: undefined,
            companyAnnouncements: false,
            companyAnnouncementsId: undefined,
            shiftUpdates: false,
            shiftUpdatesId: undefined,
            attendanceUpdates: false,
            attendanceUpdatesId: undefined,
            profileUpdates: false,
            profileUpdatesId: undefined,
            quietDays: "",
            quietDaysId: undefined,
            smsDailyShiftReminders: false,
            smsDailyShiftRemindersId: 1,
            displayAvailability: false,
            displayAvailabilityId: 3,
            showScore: false,
            showScoreId: 4,
            showSkill: false,
            showSkillId: 7,
            initialized: true,
            autoAccept: false,
            autoAcceptId: 5,
            dailyMaxHours: 0,
            dailyMaxHoursId: 6,
            workerAttendanceAcknowledgement: false,
            workerAttendanceAcknowledgementId: 0,
            showShiftAutoAccept: false,
            showShiftAutoAcceptId: 99,
            showWhatsAppToggle: false,
            workerProfileInfo: undefined,
            workerProfileInfoId: 0,
            companyId: undefined,
            workerTypeName: FLEX_WORKER,
            workerTypeText: "Flex",
            maxWorkHours: 0,
            remainingWorkHours: 0,
            showWeeklyHoursLimitNoticeOn: 0.2,
            fullTimeShiftSwapCancelEnabled: false,
            timeOffEnabled: false,
            customizedBrandingEnabled: false,
            languagePreferenceId: 27,
            languagePreference: undefined,
            workerSubTypeEnabled: false,
            fullTimeSchedulingEnabled: false,
            workerSetPrefEnabled: false,
            utilityWorker: false,
            vakaySSOLinkEnabled: false,
            workerEvaluationEnabled: false,
          };
          responseArray.forEach((setting) => {
            switch (setting["code"]) {
              case "vakaySSOLink.enabled":
                data["vakaySSOLinkEnabled"] = setting["value"] === true;
                break;
              case "DailyReminderSMS":
                // Daily shift reminders
                data["smsDailyShiftReminders"] = setting["value"] === true;
                data["smsDailyShiftRemindersId"] = setting["id"];
                break;
              case "SMSNotifications":
                // SMS Notifications
                data["smsNotifications"] = setting["value"] === true;
                data["smsNotificationsId"] = setting["id"];
                break;
              case "ShowSmsToggle":
                // Show/hide the SMS notifications toggle in settings
                data["showSmsToggle"] = setting["value"] === true;
                break;
              case "WhatsAppNotifications":
                data["whatsappNotifications"] = setting["value"] === true;
                data["whatsappNotificationsId"] = setting["id"];
                break;
              case "ShowWhatsAppToggle":
                // Show/hide the WhatsApp notifications toggle in settings
                data["showWhatsAppToggle"] = setting["value"] === true;
                break;
              case "NCat_CompanyAnnouncements":
                data["companyAnnouncements"] = setting["value"] === true;
                data["companyAnnouncementsId"] = setting["id"];
                break;
              case "NCat_ShiftUpdates":
                data["shiftUpdates"] = setting["value"] === true;
                data["shiftUpdatesId"] = setting["id"];
                break;
              case "NCat_AttendanceUpdates":
                data["attendanceUpdates"] = setting["value"] === true;
                data["attendanceUpdatesId"] = setting["id"];
                break;
              case "NCat_ProfileUpdates":
                data["profileUpdates"] = setting["value"] === true;
                data["profileUpdatesId"] = setting["id"];
                break;
              case "NonWorkingDays":
                // Quiet Days: days on which shift notifications are suppressed,
                // as a comma-separated uppercase day list ("SATURDAY,SUNDAY").
                data["quietDays"] = setting["value"] ?? "";
                data["quietDaysId"] = setting["id"];
                break;
              case "ShowAvailability":
                // Availability
                data["displayAvailability"] = setting["value"] === true;
                data["displayAvailabilityId"] = setting["id"];
                break;
              case "ShowScore":
                // Score in profile
                data["showScore"] = setting["value"] === true;
                data["showScoreId"] = setting["id"];
                break;
              case "ShowSkill":
                data["showSkill"] = setting["value"] === true;
                data["showSkillId"] = setting["id"];
                break;
              case "ShiftAutoAccept":
                // Auto-accept shift
                data["autoAccept"] = setting["value"] === true;
                data["autoAcceptId"] = setting["id"];
                break;
              case "DailyMaxWorkHours":
                // Max hours to work per day
                data["dailyMaxHours"] = setting["value"];
                data["dailyMaxHoursId"] = setting["id"];
                break;
              case "WorkerAttendAck":
                // Show hide worker attendance acknowledgement
                data["workerAttendanceAcknowledgement"] = setting["value"];
                data["workerAttendanceAcknowledgementId"] = setting["id"];
                break;
              case "ShowShiftAutoAccept":
                // Show hide auto accept option
                data["showShiftAutoAccept"] = setting["value"];
                data["showShiftAutoAcceptId"] = setting["id"];
                break;
              case "WorkerProfileInfo":
                // Worker profile info
                data["workerProfileInfo"] = setting["value"];
                data["workerProfileInfoId"] = setting["id"];
                break;
              case "CompanyId":
                data["companyId"] = setting["value"];
                break;
              case "ShowWeeklyHoursLimitNoticeOn":
                // Show weekly hours limit notice on
                data["showWeeklyHoursLimitNoticeOn"] = setting["value"];
                break;
              case "ftShift.swapAndCancelEnabled":
                // Full time worker shift swap and call out enabled or not
                data["fullTimeShiftSwapCancelEnabled"] =
                  setting["value"] ?? false;
                break;
              case "attendMgmt.enabled":
                // Attendance management is enabled or not
                data["timeOffEnabled"] = setting["value"];
                break;
              case "customizedBrandingEnabled":
                // Custom logo for a company is enabled or not
                data["customizedBrandingEnabled"] = setting["value"];
                break;
              case "LanguagePreference":
                if (languagePreference !== setting["value"]) {
                  updateLanguagePreference(setting["value"]);
                }
                data["languagePreference"] = getLanguageCode(setting["value"]);
                data["languagePreferenceId"] = setting["id"];
                break;
              case "workerSubType.enabled":
                data["workerSubTypeEnabled"] = setting["value"];
                break;
              case "FullTimeSchedulingEnabled":
                data["fullTimeSchedulingEnabled"] = setting["value"];
                break;
              case "WorkerSetPrefEnabled":
                data["workerSetPrefEnabled"] = setting["value"];
                break;
              case "WorkerEvaluationEnabled":
                data["workerEvaluationEnabled"] = setting["value"] === true;
                break;
              default:
                break;
            }
          });
          return thunkAPI.fulfillWithValue(data);
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-unexpected-response"),
          });
        }
      }
    } catch (error: any) {
      Sentry.captureException(error);
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        if (Object.prototype.hasOwnProperty.call(error, "data")) {
          const errorResponse = error.response.data;
          errorResponse.status = error?.response?.status;
          if (Object.prototype.hasOwnProperty.call(errorResponse, "message")) {
            return thunkAPI.rejectWithValue(errorResponse);
          } else {
            return thunkAPI.rejectWithValue({
              message: i18next.t("worker-slice_error-occurred"),
              status: errorResponse.status,
            });
          }
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-getting-sms-settings"),
            status: error?.response?.status,
          });
        }
      } else if (Object.prototype.hasOwnProperty.call(error, "message")) {
        return thunkAPI.rejectWithValue({ message: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-getting-sms-settings"),
        });
      }
    }
  },
  {
    // Every worker route bootstraps settings from WorkerShell, and most screens
    // dispatch again on mount — plus StrictMode runs each effect twice in dev.
    // Collapse the burst: a dispatch made while a request is already in flight
    // resolves off the same response instead of hitting the network again.
    condition: (_payload, { getState }) => {
      const state = getState() as any;
      return state?.worker?.getSettings?.status !== "pending";
    },
  }
);

// Notification / auto-accept setting keys → the settings field they toggle.
// The `key` is the GetSettings code; used to route optimistic + confirm/revert
// updates for the Settings toggles.
export const SETTING_KEY_TO_FIELD: Record<string, string> = {
  SMSNotifications: "smsNotifications",
  WhatsAppNotifications: "whatsappNotifications",
  NCat_CompanyAnnouncements: "companyAnnouncements",
  NCat_ShiftUpdates: "shiftUpdates",
  NCat_AttendanceUpdates: "attendanceUpdates",
  NCat_ProfileUpdates: "profileUpdates",
  NonWorkingDays: "quietDays",
  ShiftAutoAccept: "autoAccept",
  DailyMaxWorkHours: "dailyMaxHours",
};

// Set SMS Settings
export const setSmsSettings = createAsyncThunk(
  "worker/UpdateSetting",
  async (
    {
      id,
      value,
      originalValue,
      key,
    }: { id: number; value: string; originalValue: string; key: string },
    thunkAPI
  ) => {
    try {
      const data = {
        id: id,
        value: value,
      };

      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/Workers/UpdateSetting`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response.status === 200)
        return thunkAPI.fulfillWithValue({ id: id, value: value, key: key });
    } catch (error: any) {
      Sentry.captureException(error);
      const defaultErrorMessage = "Error updating settings.";
      let errorMessage = "";
      let status = undefined;
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        if (Object.prototype.hasOwnProperty.call(error?.response, "data")) {
          const errorResponse = error.response.data;
          errorResponse.status = error?.response?.status;
          if (Object.prototype.hasOwnProperty.call(errorResponse, "message")) {
            errorMessage = errorResponse.message;
            status = errorResponse.status;
          }
        }
      } else if (Object.prototype.hasOwnProperty.call(error, "message")) {
        errorMessage = error.message;
      }
      return thunkAPI.rejectWithValue({
        message: errorMessage || defaultErrorMessage,
        key: key,
        value: originalValue,
        status: status,
      });
    }
  }
);

export const getWorkerPreferences = createAsyncThunk(
  "worker/getWorkerPreferences",
  async (payload, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
        const response = await uninterceptedAxiosInstance.get(`${process.env.REACT_APP_API_END_POINT}/Workers/Preferences`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("worker-token"),
        },
      });
      if (response.status === 200) {
        const data = response.data ? response.data : [];
        return thunkAPI.fulfillWithValue(data);
      } else if (response.status === 204) {
        return thunkAPI.fulfillWithValue([]);
      } else {
        return thunkAPI.rejectWithValue({
          message: i18n.t("worker-slice_error-occurred"),
        });
      }
    } catch (error: any) {
      Sentry.captureException(error);
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        if (Object.prototype.hasOwnProperty.call(error, "data")) {
          const errorResponse = error.response.data;
          errorResponse.status = error?.response?.status;
          if (Object.prototype.hasOwnProperty.call(errorResponse, "message")) {
            return thunkAPI.rejectWithValue(errorResponse);
          } else {
            return thunkAPI.rejectWithValue({
              message: i18next.t("worker-slice_error-occurred"),
              status: errorResponse.status,
            });
          }
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-occurred"),
            status: error?.response?.status,
          });
        }
      } else if (Object.prototype.hasOwnProperty.call(error, "message")) {
        return thunkAPI.rejectWithValue({ message: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-occurred"),
        });
      }
    }
  }
);

export const saveWorkerPreferences = createAsyncThunk(
  "worker/saveWorkerPreferences",
  async (payload, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.put(`${process.env.REACT_APP_API_END_POINT}/Workers/Preferences`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("worker-token"),
        },
      });
      if (response.status === 200 || response.status === 201) {
        return thunkAPI.fulfillWithValue(payload);
      } else {
        return thunkAPI.rejectWithValue({
          message: i18n.t("worker-slice_error-occurred"),
        });
      }
    } catch (error: any) {
      Sentry.captureException(error);
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        if (Object.prototype.hasOwnProperty.call(error, "data")) {
          const errorResponse = error.response.data;
          errorResponse.status = error?.response?.status;
          if (Object.prototype.hasOwnProperty.call(errorResponse, "message")) {
            return thunkAPI.rejectWithValue(errorResponse);
          } else {
            return thunkAPI.rejectWithValue({
              message: i18next.t("worker-slice_error-occurred"),
              status: errorResponse.status,
            });
          }
        } else {
          return thunkAPI.rejectWithValue({
            message: i18next.t("worker-slice_error-occurred"),
            status: error?.response?.status,
          });
        }
      } else if (Object.prototype.hasOwnProperty.call(error, "message")) {
        return thunkAPI.rejectWithValue({ message: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          message: i18next.t("worker-slice_error-occurred"),
        });
      }
    }
  }
);

export const fetchLeaveTypes = createAsyncThunk(
  "manageWorkers/leaveTypes",
  async (payload, { fulfillWithValue, rejectWithValue, getState }) => {
    try {
      const companyId = (getState() as any).worker.settings.companyId as string;
      const uninterceptedAxiosInstance = axios.create();
      const getLeaveTypes = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/leaves/types/${companyId}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (getLeaveTypes.status === 200) {
        return fulfillWithValue(getLeaveTypes.data);
      } else if (getLeaveTypes.status === 204) {
        return fulfillWithValue([]);
      }
      return rejectWithValue({
        message: i18n.t("worker-slice_error-occurred"),
      });
    } catch (error: any) {
      if (error.error === "CanceledError" || error.message === "canceled") {
        // Ignore the CanceledError and do not log or handle it further
        console.log("Request was canceled");
        return rejectWithValue({ canceled: true });
      }
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        if (error.response?.status === 429) {
          const errorResponse = {
            ...error.response.data,
            status: error.response.status,
          };
          return rejectWithValue(errorResponse);
        } else {
          return rejectWithValue({
            message: i18n.t("worker-slice_error-occurred"),
          });
        }
      } else if (Object.prototype.hasOwnProperty.call(error, "message")) {
        return rejectWithValue({ message: error.message });
      } else {
        return rejectWithValue({
          message: i18n.t("worker-slice_error-occurred"),
        });
      }
    }
  }
);

export const fetchLeaveRequests = createAsyncThunk(
  "manageWorkers/leaveRequests",
  async (payload: any, { fulfillWithValue, rejectWithValue, getState }) => {
    try {
      const companyId = (getState() as any).worker.settings.companyId as string;
      const uninterceptedAxiosInstance = axios.create();
      const getLeaveRequests = await uninterceptedAxiosInstance.get(
        `${process.env.REACT_APP_API_END_POINT}/workers/requests?companyId=${companyId}&startDate=${payload.startDate}&endDate=${payload.endDate}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (getLeaveRequests.status === 200) {
        return fulfillWithValue(getLeaveRequests.data);
      } else if (getLeaveRequests.status === 204) {
        return fulfillWithValue([]);
      }
      return rejectWithValue({
        message: i18n.t("worker-slice_error-occurred"),
      });
    } catch (error: any) {
      if (error.error === "CanceledError" || error.message === "canceled") {
        // Ignore the CanceledError and do not log or handle it further
        console.log("Request was canceled");
        return rejectWithValue({ canceled: true });
      }
      if (
        Object.prototype.hasOwnProperty.call(error, "response") &&
        Object.prototype.hasOwnProperty.call(error.response, "status")
      ) {
        if (error.response?.status === 429) {
          const errorResponse = {
            ...error.response.data,
            status: error.response.status,
          };
          return rejectWithValue(errorResponse);
        } else {
          return rejectWithValue({
            message: i18n.t("worker-slice_error-occurred"),
          });
        }
      } else if (Object.prototype.hasOwnProperty.call(error, "message")) {
        return rejectWithValue({ message: error.message });
      } else {
        return rejectWithValue({
          message: i18n.t("worker-slice_error-occurred"),
        });
      }
    }
  }
);

export const fetchRequestTypes = createAsyncThunk(
  "manageWorkers/requestTypes",
  async (payload, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const getRequestTypes = await uninterceptedAxiosInstance.get(`${process.env.REACT_APP_API_END_POINT}/workers/requestTypes`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("worker-token"),
        },
      });
      if (getRequestTypes.status === 200) {
        return thunkAPI.fulfillWithValue(getRequestTypes.data);
      } else if (getRequestTypes.status === 204) {
        return thunkAPI.fulfillWithValue([]);
      }
      return thunkAPI.rejectWithValue({
        message: i18n.t("worker-slice_error-occurred"),
      });
    } catch (error:any) {
      if (error.error === "CanceledError" || error.message === "canceled") {
        // Ignore the CanceledError and do not log or handle it further
        console.log("Request was canceled");
        return thunkAPI.rejectWithValue({canceled: true});
      }
      if (
        Object.prototype.hasOwnProperty.call(error, "response") &&
        Object.prototype.hasOwnProperty.call(error.response, "status")
      ) {
        if (error.response?.status === 429) {
          const errorResponse = {
            ...error.response.data,
            status: error.response.status,
          };
          return thunkAPI.rejectWithValue(errorResponse);
        } else {
          return thunkAPI.rejectWithValue({
            message: i18n.t("worker-slice_error-occurred"),
          });
        }
      } else if (Object.prototype.hasOwnProperty.call(error,"message")) {
        return thunkAPI.rejectWithValue({message: error.message});
      } else {
        return thunkAPI.rejectWithValue({
          message: i18n.t("worker-slice_error-occurred"),
        });
      }
    }
  },
);


export const submitLeaveRequest = createAsyncThunk(
  "worker/submitLeaveRequest",
  async (payload, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(`${process.env.REACT_APP_API_END_POINT}/workers/request`, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: localStorage.getItem("worker-token"),
        },
      });
      if (response.status === 200) {
        return thunkAPI.fulfillWithValue(payload);
      }
      return thunkAPI.rejectWithValue({
        message: i18n.t("worker-slice_error-submitting-time-off-request"),
      });
    } catch (error:any) {
      if (error.error === "CanceledError" || error.message === "canceled") {
        // Ignore the CanceledError and do not log or handle it further
        console.log("Request was canceled");
        return thunkAPI.rejectWithValue({canceled: true});
      }
      Sentry.captureException(error);
      if ( Object.prototype.hasOwnProperty.call(error, "response")) {
        if (error.response?.status === 429) {
          if (Object.prototype.hasOwnProperty.call(error.response,"data")) {
            const errorResponse = error.response.data;
            errorResponse.status = error.response.status;
            if (Object.prototype.hasOwnProperty.call(errorResponse,"message")) {
              return thunkAPI.rejectWithValue(errorResponse);
            } else {
              return thunkAPI.rejectWithValue({
                message: i18n.t("worker-slice_error-occurred"),
                status: errorResponse.status,
              });
            }
          } else {
            return thunkAPI.rejectWithValue({
              message: i18n.t("worker-slice_error-submitting-time-off-request"),
              status: error.response?.status,
            });
          }
        } else {
          return thunkAPI.rejectWithValue({
            message: i18n.t("worker-slice_error-submitting-time-off-request"),
          });
        }
      } else if (Object.prototype.hasOwnProperty.call(error,"message")) {
        return thunkAPI.rejectWithValue({message: error.message});
      } else {
        return thunkAPI.rejectWithValue({
          message: i18n.t("worker-slice_error-submitting-time-off-request"),
        });
      }
    }
  },
);

export const submitLeaveRequestBatch = createAsyncThunk(
  "worker/submitLeaveRequestBatch",
  async (payload: any, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/workers/batch-requests`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (response.status === 200) {
        // Echo the submitted shifts so the UI can refresh / mark them pending.
        return thunkAPI.fulfillWithValue({
          createdIds: Array.isArray(response.data) ? response.data : [],
          shifts: payload?.shifts ?? [],
        });
      }
      return thunkAPI.rejectWithValue({
        message: i18n.t("worker-slice_error-submitting-time-off-request"),
      });
    } catch (error: any) {
      if (error.error === "CanceledError" || error.message === "canceled") {
        console.log("Request was canceled");
        return thunkAPI.rejectWithValue({ canceled: true });
      }
      Sentry.captureException(error);
      if (Object.prototype.hasOwnProperty.call(error, "response")) {
        if (error.response?.status === 429) {
          const errorResponse = error.response?.data ?? {};
          errorResponse.status = error.response.status;
          if (
            Object.prototype.hasOwnProperty.call(errorResponse, "message")
          ) {
            return thunkAPI.rejectWithValue(errorResponse);
          }
          return thunkAPI.rejectWithValue({
            message: i18n.t("worker-slice_error-occurred"),
            status: errorResponse.status,
          });
        }
        // Batch is fail-fast and all-or-nothing — surface the backend's
        // shift-specific "Shift N: ..." message verbatim on 400.
        const backendMessage = error.response?.data?.message;
        return thunkAPI.rejectWithValue({
          message: backendMessage
            ? backendMessage
            : i18n.t("worker-slice_error-submitting-time-off-request"),
        });
      } else if (Object.prototype.hasOwnProperty.call(error, "message")) {
        return thunkAPI.rejectWithValue({ message: error.message });
      }
      return thunkAPI.rejectWithValue({
        message: i18n.t("worker-slice_error-submitting-time-off-request"),
      });
    }
  }
);

export const withdrawLeaveRequest = createAsyncThunk(
  "manageWorkers/withdrawRequest",
  async (payload :any, thunkAPI) => {
    try {
      const uninterceptedAxiosInstance = axios.create();
      const withdrawLeaveRequests = await uninterceptedAxiosInstance.delete(
        `${process.env.REACT_APP_API_END_POINT}/workers/request/${payload}`,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("worker-token"),
          },
        }
      );
      if (withdrawLeaveRequests.status === 200) {
        return thunkAPI.fulfillWithValue(withdrawLeaveRequests.data);
      }
      return thunkAPI.rejectWithValue({
        message: i18n.t("worker-slice_error-occurred"),
      });
    } catch (error: any) {
      if (error.error === "CanceledError" || error.message === "canceled") {
        // Ignore the CanceledError and do not log or handle it further
        console.log("Request was canceled");
        return thunkAPI.rejectWithValue({ canceled: true });
      }
      if (
        Object.prototype.hasOwnProperty.call(error, "response") &&
        Object.prototype.hasOwnProperty.call(error.response, "status")
      ) {
        if (error.response?.status === 429) {
          const errorResponse = {
            ...error.response.data,
            status: error.response.status,
          };
          return thunkAPI.rejectWithValue(errorResponse);
        } else {
          return thunkAPI.rejectWithValue({
            message: i18n.t("worker-slice_error-occurred"),
          });
        }
      } else if (Object.prototype.hasOwnProperty.call(error,"message")) {
        return thunkAPI.rejectWithValue({ message: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          message: i18n.t("worker-slice_error-occurred"),
        });
      }
    }
  }
);

// import moduleName from 'a'
export const assertVakaySSO = createAsyncThunk(
  "worker/AssertVakaySSO",
  async (_, thunkAPI) => {
    try {
      const workerToken = localStorage.getItem("worker-token");
      const uninterceptedAxiosInstance = axios.create();
      const response = await uninterceptedAxiosInstance.post(
        `${process.env.REACT_APP_API_END_POINT}/internalsso/assert`,
        {},
        {
          headers: {
            "Content-type": "application/json",
            Authorization: workerToken,
          },
        }
      );
      if (response.status === 200) {
        return thunkAPI.fulfillWithValue(response.data);
      }
    } catch (error: any) {
      Sentry.captureException(error);
      return thunkAPI.rejectWithValue(error?.response?.data);
    }
  }
);

export const WebWorkerSlice = createSlice({
  name: "worker",
  initialState: {
    settings: {
      smsNotifications: false,
      smsNotificationsId: 2,
      showSmsToggle: false,
      whatsappNotifications: false,
      whatsappNotificationsId: undefined,
      companyAnnouncements: false,
      companyAnnouncementsId: undefined,
      shiftUpdates: false,
      shiftUpdatesId: undefined,
      attendanceUpdates: false,
      attendanceUpdatesId: undefined,
      profileUpdates: false,
      profileUpdatesId: undefined,
      quietDays: "",
      quietDaysId: undefined,
      smsDailyShiftReminders: false,
      smsDailyShiftRemindersId: 1,
      // Show availability to worker
      displayAvailability: false,
      displayAvailabilityId: 3,
      // Show score to worker
      showScore: false,
      showScoreId: 4,
      // Show skill to worker
      showSkill: false,
      showSkillId: 7,
      // Setting fetched once
      initialized: false,
      autoAccept: false,
      autoAcceptId: 5,
      dailyMaxHours: 0,
      dailyMaxHoursId: 6,
      // Setting from device
      pushNotification: false,
      workerAttendanceAcknowledgement: false,
      showShiftAutoAccept: false,
      showWhatsAppToggle: false,
      workerProfileInfo: undefined,
      remainingWorkHours: 0,
      maxWorkHours: 0,
      workerTypeName: FLEX_WORKER,
      workerTypeText: "Flex",
      companyId: undefined,
      showWeeklyHoursLimitNoticeOn: 0.2,
      fullTimeShiftSwapCancelEnabled: false,
      timeOffEnabled: false,
      customizedBrandingEnabled: false,
      showShiftAutoAcceptId: 99,
      workerProfileInfoId: 0,
      workerSubTypeEnabled: false,
      vakaySSOLinkEnabled: false,
    },
    assertVakaySSO: {
      status: "idle",
      errorMessage: "",
      redirectUrl: "",
    },
    leaveRequestPage: {
      status: "idle",
      errorMessage: "",
      isOpen: false,
      shift: null,
      type: null,
      availableLeaveTypes: null,
    },
    applyTimeOffPage: {
      isOpen: false,
    },
    getSettings: {
      status: "idle",
      errorMessage: "",
    },
    setSmsSettings: {
      status: "idle",
      errorMessage: "",
    },
    workerId: "",
    workerType: FLEX_WORKER,
    location: 0,
    skillLevels: [],
    workerData: {
      firstName:
        JSON.parse(localStorage.getItem("worker-details") || "{}")?.firstName ||
        "",
      lastName:
        JSON.parse(localStorage.getItem("worker-details") || "{}")?.lastName ||
        "",
      contact:
        JSON.parse(localStorage.getItem("worker-details") || "{}")
          ?.WorkerContact || "",
    },
    workerProfile: {
      data: {},
      status: "idle",
      errorMessage: "",
    },
    profileSkills: {
      jobs: [] as any[],
      otherSkills: [] as any[],
      status: "idle",
      errorMessage: "",
    },
    workHistory: {
      attendedShifts: [] as any[],
      otMinutes: 0,
      status: "idle",
      errorMessage: "",
    },
    offerShiftPage: {
      isOpen: false,
      shift: null as any,
    },
    createSwapRequest: {
      status: "idle",
      errorMessage: "",
      response: null as any,
    },
    getNotifications: {
      status: "idle",
      errorMessage: "",
      notifications: [] as any[],
    },
    shiftsConfirmed: [] as Shift[],
    authenticated: false,
    login: {
      status: "idle",
      errorMessage: "",
    },
    getPasscode: {
      status: "idle",
      errorMessage: "",
      errorCode: "",
    },
    verifyPasscode: {
      status: "idle",
      errorMessage: "",
      isLocked: false,
    },
    Refresh: {
      status: "idle",
      errorMessage: "",
    },
    upcomingShifts: [] as Shift[],
    getConfirmedShifts: {
      status: "idle",
      errorMessage: "",
      canLoadMore: true,
      loadedTill: ""
    },
    cancelShift: {
      status: "idle",
      errorMessage: "",
      type: "C",
      response: undefined,
      startDateTime: undefined,
      endDateTime: undefined,
    } as {
      status: string;
      errorMessage: string;
      type: string;
      response:
        | {
            details: string;
            message: string;
          }
        | undefined;
      startDateTime: string | undefined;
      endDateTime: string | undefined;
    },
    forceCancelShift: {
      status: "idle",
      errorMessage: "",
    },
    getUpcomingShifts: {
      status: "idle",
      errorMessage: "",
      canLoadMore: true,
      loadedTill: ""
    },
    confirmShift: {
      status: "idle",
      errorMessage: "",
      weeklyHoursLimitMessage: "",
      continuousWorkDayLimitMessage: "",
      acceptanceWindowClosed: false,
      shifts: [],
      lastConfirmedShiftDetails: undefined as {
        status: string;
        startDateTime: string;
        endDateTime: string;
        jobOrSkills: string;
      } | undefined,
    },
    viewShift: {
      status: "idle",
      errorMessage: "",
      viewedShifts: [] as any[],
    },
    markShiftsStatus: {
      status: "idle",
      errorMessage: "",
      viewedShifts: [] as any[],
    },
    declineShift: {
      status: "idle",
      errorMessage: "",
      startDateTime: undefined,
      endDateTime: undefined,
    },
    signupShiftDialog: {
      isOpen: false,
      shiftId: null,
      swap: false,
      swapId: null,
    },
    isCancelShiftPageOpen: false,
    isWorkerPreferencePageOpen: false,
    partialShiftSignUp: {
      isOpen: false,
      shiftId: null,
      swap: false,
      swapId: null,
    },
    flexiShiftSignUp: {
      isOpen: false,
      shiftId: null,
      swap: false,
      swapId: null,
    },
    viewShiftDialog: {
      isOpen: false,
      shiftId: null,
      swap: false,
      status: "",
    },
    isAcknowledgementPageOpen: false,
    viewRegularShiftPage: {
      isOpen: false,
      shift: undefined,
    },
    getSkillLevels: {
      status: "idle",
      errorMessage: "",
    },
    getRegularShifts: {
      status: "idle",
      errorMessage: "",
      canLoadMore: true,
      loadedTill: ""
    },
    regularShifts: [] as Shift[],
    customLogo: "",
    getCustomLogo: {
      status: "idle",
      errorMessage: "",
      response: [], // Do not use this directly for logo
    },
    getWorkerPreferences: {
      status: "idle",
      errorMessage: "",
      utilityWorker: false,
    },
    jobPreferences: [],
    shiftPreferences: [],
    primaryJobName: "-",
    yourShift: "-",
    saveWorkerPreferences: {
      status: "idle",
      errorMessage: "",
    },
    getAcknowledgements: {
      status: "idle",
      errorMessage: "",
    },
    acknowledgements: [] as { attendStatus: string }[],
    acknowledgeShift: {
      status: "idle",
      errorMessage: "",
      data: undefined,
    },
    getCancelShiftReasons: {
      status: "idle",
      errorMessage: "",
    },
    cancelShiftReasons: [],
    getSpotsLeft: {
      status: "idle",
      errorMessage: "",
    },
    workerRateLimitError: {
      isErrorOccurred: false,
    },
    getLeaveTypes: {
      status: "idle",
      errorMessage: "",
      leaveTypes: [],
    },
    getRequestTypes: {
      status: "idle",
      errorMessage: "",
      requestTypes: [],
    },
    getLeaveRequests: {
      status: "idle",
      errorMessage: "",
      leaveRequests: [],
    },
    submitLeaveRequest: {
      status: "idle",
      errorMessage: "",
      ftShiftId: "",
      startDateTime: undefined,
      endDateTime: undefined,
    },
    submitLeaveRequestBatch: {
      status: "idle",
      errorMessage: "",
      createdIds: [] as number[],
      shifts: [] as any[],
    },
    withdrawLeaveRequest: {
      status: "idle",
      errorMessage: "",
      ftShiftId: "",
    },
    ftSchedulingStatus:{
      status: "idle",
      errorMessage: "",
      schedulingStatus: undefined
    }
  },
  reducers: {
    setWorker: (state) => {
      state.authenticated = true;
    },
    setCustomLogo: (state, action) => {
      state.customLogo = action.payload;
    },
    setWorkerId: (state, action) => {
      state.workerId = action.payload;
    },
    setTabToViewShifts: (state) => {
      state.location = 1;
    },
    setTabToShiftSignup: (state) => {
      state.location = 0;
    },
    setTab: (state, action) => {
      state.location = action.payload;
    },
    setIsCancelShiftPageOpen: (state, action) => {
      state.isCancelShiftPageOpen = action.payload;
    },
    setWorkerPreferencePageOpen: (state, action) => {
      state.isWorkerPreferencePageOpen = action.payload;
    },
    openPartialShiftSignup: (state, action) => {
      const { id, swap, swapId } = action.payload;
      state.partialShiftSignUp.isOpen = true;
      state.confirmShift.status = "idle";
      state.confirmShift.errorMessage = "";
      state.confirmShift.acceptanceWindowClosed = false;
      state.partialShiftSignUp.shiftId = id;
      state.partialShiftSignUp.swap = swap;
      state.partialShiftSignUp.swapId = swapId;
    },
    closePartialShiftSignup: (state) => {
      state.partialShiftSignUp.isOpen = false;
      state.partialShiftSignUp.shiftId = null;
    },
    openFlexiShiftSignup: (state, action) => {
      const { id, swap, swapId } = action.payload;
      state.flexiShiftSignUp.isOpen = true;
      state.confirmShift.status = "idle";
      state.confirmShift.errorMessage = "";
      state.confirmShift.acceptanceWindowClosed = false;
      state.flexiShiftSignUp.shiftId = id;
      state.flexiShiftSignUp.swap = swap;
      state.flexiShiftSignUp.swapId = swapId;
      state.confirmShift.weeklyHoursLimitMessage = "";
      state.confirmShift.continuousWorkDayLimitMessage = "";
      state.confirmShift.shifts = [];
    },
    closeFlexiShiftSignup: (state) => {
      state.flexiShiftSignUp.isOpen = false;
      state.flexiShiftSignUp.shiftId = null;
      state.confirmShift.weeklyHoursLimitMessage = "";
      state.confirmShift.continuousWorkDayLimitMessage = "";
      state.confirmShift.shifts = [];
    },
    openLeaveRequestPage: (state, action) => {
      const { shift, type, availableLeaveTypes } = action.payload;
      state.leaveRequestPage.isOpen = true;
      state.leaveRequestPage.status = "idle";
      state.leaveRequestPage.errorMessage = "";
      // state.viewRegularShiftDialog.isOpen = false;
      state.leaveRequestPage.type = type;
      state.leaveRequestPage.shift = shift;
      state.leaveRequestPage.availableLeaveTypes = availableLeaveTypes;
    },
    resetSubmitLeaveRequest: (state) => {
      state.submitLeaveRequest.status = "idle";
      state.submitLeaveRequest.errorMessage = "";
    },
    resetPreSubmitLeaveRequest: (state) => {
      state.submitLeaveRequest.ftShiftId = "";
      state.submitLeaveRequest.startDateTime = undefined;
      state.submitLeaveRequest.endDateTime = undefined;
    },
    presetSubmitLeaveRequest: (state, action) => {
      state.submitLeaveRequest.ftShiftId = action.payload.ftShiftId;
      state.submitLeaveRequest.startDateTime = action.payload.startDateTime;
      state.submitLeaveRequest.endDateTime = action.payload.endDateTime;
    },
    setWidthdrawLeaveRequestFtShiftId: (state, action) => {
      state.withdrawLeaveRequest.ftShiftId = action.payload;
    },
    resetWithdrawLeaveRequest: state => {
      return {
        ...state,
        withdrawLeaveRequest: {
          status: "idle",
          errorMessage: "",
          ftShiftId: "",
        },
      };
    },
    closeLeaveRequestPage: (state) => {
      state.leaveRequestPage.isOpen = false;
      state.leaveRequestPage.type = null;
      state.leaveRequestPage.shift = null;
      state.leaveRequestPage.availableLeaveTypes = null;
    },
    openApplyTimeOff: (state) => {
      state.applyTimeOffPage.isOpen = true;
    },
    closeApplyTimeOff: (state) => {
      state.applyTimeOffPage.isOpen = false;
    },
    openOfferShiftPage: (state, action) => {
      state.offerShiftPage.isOpen = true;
      state.offerShiftPage.shift = action.payload;
      state.createSwapRequest.status = "idle";
      state.createSwapRequest.errorMessage = "";
    },
    closeOfferShiftPage: (state) => {
      state.offerShiftPage.isOpen = false;
      state.offerShiftPage.shift = null;
    },
    resetCreateSwapRequest: (state) => {
      state.createSwapRequest.status = "idle";
      state.createSwapRequest.errorMessage = "";
      state.createSwapRequest.response = null;
    },
    setSettingsManually: (state, action) => {
      // Optimistic settings update: flip the toggle immediately; the thunk
      // confirms on success or reverts on failure.
      const { key, value } = action.payload;
      const field = SETTING_KEY_TO_FIELD[key];
      if (field) {
        state.settings[field] = value;
      }
    },
    resetSubmitLeaveRequestBatch: (state) => {
      state.submitLeaveRequestBatch.status = "idle";
      state.submitLeaveRequestBatch.errorMessage = "";
      state.submitLeaveRequestBatch.createdIds = [];
      state.submitLeaveRequestBatch.shifts = [];
    },
    resetVerifyPasscode: (state) => {
      state.verifyPasscode.status = "idle";
      state.verifyPasscode.errorMessage = "";
      state.verifyPasscode.isLocked = false;
    },
    setIsAcknowledgementPageOpen: (state, action) => {
      state.isAcknowledgementPageOpen = action.payload;
    },
    resetCancelShift: (state) => {
      state.cancelShift.status = "idle";
      state.cancelShift.errorMessage = "";
      state.cancelShift.type = "C";
      state.cancelShift.response = undefined;
      state.cancelShift.startDateTime = undefined;
      state.cancelShift.endDateTime = undefined;
    },
    resetConfirmShift: (state) => {
      state.confirmShift.status = "idle";
      state.confirmShift.errorMessage = "";
      state.confirmShift.acceptanceWindowClosed = false;
      state.confirmShift.lastConfirmedShiftDetails = undefined;
    },
    resetUpcomingShifts: (state) => {
      state.getUpcomingShifts.status = "idle";
      state.getUpcomingShifts.errorMessage = "";
    },
    resetGetRegularShifts: (state) => {
      state.getRegularShifts.status = "idle";
      state.getRegularShifts.errorMessage = "";
      state.getRegularShifts.canLoadMore = true;
    },
    resetGetSettings: (state) => {
      state.getSettings.status = "idle";
      state.getSettings.errorMessage = "";
    },
    openSignupShiftDialog: (state, action) => {
      const { id, swap, swapId } = action.payload;
      state.signupShiftDialog.isOpen = true;
      state.confirmShift.status = "idle";
      state.confirmShift.errorMessage = "";
      state.confirmShift.acceptanceWindowClosed = false;
      state.confirmShift.lastConfirmedShiftDetails = undefined;
      state.signupShiftDialog.shiftId = id;
      state.signupShiftDialog.swap = swap;
      state.signupShiftDialog.swapId = swapId;
      state.confirmShift.weeklyHoursLimitMessage = "";
      state.confirmShift.continuousWorkDayLimitMessage = "";
    },
    closeSignupShiftDialog: (state) => {
      state.signupShiftDialog.isOpen = false;
      state.signupShiftDialog.shiftId = null;
      state.confirmShift.weeklyHoursLimitMessage = "";
      state.confirmShift.continuousWorkDayLimitMessage = "";
    },

    openViewShiftDialog: (state, action) => {
      state.viewShiftDialog.isOpen = true;
      state.viewShiftDialog.shiftId = action.payload.id;
      state.viewShiftDialog.swap = action.payload.swap;
      state.viewShiftDialog.status = action.payload.status;
    },
    openViewRegularShiftPage: (state, action) => {
      state.viewRegularShiftPage.isOpen = true;
      state.viewRegularShiftPage.shift = action.payload;
    },
    closeViewShiftDialog: (state) => {
      state.viewShiftDialog.isOpen = false;
      state.viewShiftDialog.shiftId = null;
      state.viewShiftDialog.swap = true;
      state.viewShiftDialog.status = "";
    },
    closeViewRegularShiftPage: (state) => {
      state.viewRegularShiftPage.isOpen = false;
      state.viewRegularShiftPage.shift = undefined;
    },
    logout: (state) => {
      state.authenticated = false;
      state.getPasscode.status = "idle";
      state.getPasscode.errorMessage = "";
      state.getPasscode.errorCode = "";
      state.verifyPasscode.status = "idle";
      state.verifyPasscode.errorMessage = "";
      state.verifyPasscode.isLocked = false;
      state.getCustomLogo.status = "idle";
      state.getCustomLogo.errorMessage = "";
      state.getCustomLogo.response = [];
      state.customLogo = "";
      state.workerData.firstName = "";
      state.workerData.lastName = "";
      state.workerData.contact = "";
      state.shiftsConfirmed = [];
      state.upcomingShifts = [];
      state.getConfirmedShifts.status = "idle";
      state.getConfirmedShifts.errorMessage = "";
      state.getConfirmedShifts.canLoadMore = true;
      state.getRegularShifts.status = "idle";
      state.getRegularShifts.errorMessage = "";
      state.getRegularShifts.canLoadMore = true;
      state.getUpcomingShifts.status = "idle";
      state.getUpcomingShifts.errorMessage = "";
      state.getUpcomingShifts.canLoadMore = true;
      state.getConfirmedShifts.loadedTill = "";
      state.getRegularShifts.loadedTill = "";
      state.getUpcomingShifts.loadedTill = "";
      localStorage.removeItem("worker-token");
      localStorage.removeItem("worker-refreshToken");
      localStorage.removeItem("worker-details");
      state.Refresh.status = "idle";
      state.Refresh.errorMessage = "";
    },
    resetAcknowledgements: (state) => {
      return {
        ...state,
        getAcknowledgements: {
          status: "idle",
          errorMessage: "",
        },
        acknowledgements: [],
      };
    },
    resetAcknowledgeShift: (state) => {
      return {
        ...state,
        acknowledgeShift: {
          status: "idle",
          errorMessage: "",
          data: undefined,
        },
      };
    },
    setWorkerRateLimitError: (state) => {
      state.workerRateLimitError.isErrorOccurred = true;
    },
    resetWorkerRateLimitError: (state) => {
      state.workerRateLimitError.isErrorOccurred = false;
    },
    resetAssertVakaySSO: (state) => {
      state.assertVakaySSO.status = "idle";
      state.assertVakaySSO.errorMessage = "";
      state.assertVakaySSO.redirectUrl = "";
    },
    resetSaveWorkerPreferences: state => {
      state.saveWorkerPreferences.status = "idle";
      state.saveWorkerPreferences.errorMessage = "";
    },
    resetGetWorkerPreferences: state => {
      state.getWorkerPreferences.status = "idle";
      state.getWorkerPreferences.errorMessage = "";
      state.getWorkerPreferences.utilityWorker = false;
    },
    resetDeclineShift: state => {
      state.declineShift.status = "idle";
      state.declineShift.errorMessage = "";
      state.declineShift.startDateTime = undefined;
      state.declineShift.endDateTime = undefined;
    },
    resetUpcomingShiftsLoadedTill: (state) => {
      state.getUpcomingShifts.loadedTill = "";
      state.getUpcomingShifts.status = "idle";
    },
    resetRegularShiftsLoadedTill: (state) => {
      state.getRegularShifts.loadedTill = "";
    },
    resetConfirmShiftsLoadedTill: (state) => {
      state.getConfirmedShifts.loadedTill = "";
    },
  },
  extraReducers: (builder) => {
    builder.addCase(assertVakaySSO.pending, (state) => {
      state.assertVakaySSO.status = "pending";
    });
    builder.addCase(assertVakaySSO.fulfilled, (state, action) => {
      state.assertVakaySSO.status = "fulfilled";
      state.assertVakaySSO.redirectUrl = action.payload.redirectUrl;
    });
    builder.addCase(assertVakaySSO.rejected, (state, action: any) => {
      state.assertVakaySSO.status = "error";
      state.assertVakaySSO.errorMessage = action.payload?.message;
    });

    // getPasscode
    builder.addCase(getPasscode.fulfilled, (state, action) => {
      state.authenticated = false;
      state.getPasscode.errorMessage = "";
      state.getPasscode.status = "fulfilled";
      state.getPasscode.errorCode = "";
    });
    builder.addCase(getPasscode.pending, (state, action) => {
      state.getPasscode.status = "pending";
      state.getPasscode.errorMessage = "";
      state.getPasscode.errorCode = "";
      state.authenticated = false;
    });
    builder.addCase(getPasscode.rejected, (state, action: any) => {
      try {
        if (action.payload?.status === 429) {
          state.workerRateLimitError.isErrorOccurred = true;
          state.getPasscode.status = "error";
        } else if (action.payload?.status === 400) {
          state.getPasscode.errorCode = action.payload?.code;
          state.getPasscode.status =
            action.payload?.code === "WORKER_NOT_FOUND" ? "register" : "error";
          state.getPasscode.errorMessage =
            action.payload?.message ?? i18n.t("worker-slice_error-occurred");
          Sentry.captureMessage(
            `Login attempt failed - ${action.payload?.WorkerContact}`
          );
        } else {
          state.getPasscode.status = "error";
          state.getPasscode.errorMessage =
            action.payload?.message ?? i18n.t("worker-slice_error-occurred");
        }
      } catch (error) {
        Sentry.captureException(error);
        state.getPasscode.errorMessage = i18n.t("worker-slice_error-occurred");
      }
    });
    // Refresh
    builder.addCase(Refresh.fulfilled, (state, action) => {
      state.authenticated = true;
      state.Refresh.status = "fulfilled";
      if (Object.keys(action.payload).length > 0) {
        localStorage.setItem("worker-token", action.payload?.token);
        localStorage.setItem(
          "worker-refreshToken",
          action.payload?.refreshToken
        );
      }
      const details = JSON.parse(localStorage.getItem("worker-details") || "{}");
      if (details.firstName) {
        state.workerData.firstName = details.firstName;
        state.workerData.lastName = details.lastName;
        state.workerData.contact = details.WorkerContact;
      }
    });
    builder.addCase(Refresh.pending, (state, action) => {
      state.Refresh.status = "pending";
    });
    builder.addCase(Refresh.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
      }
      // Logout if refresh failed
      state.authenticated = false;
      state.getPasscode.status = "idle";
      state.getPasscode.errorMessage = "";
      state.getPasscode.errorCode = "";
      state.verifyPasscode.status = "idle";
      state.verifyPasscode.errorMessage = "";
      state.verifyPasscode.isLocked = false;
      state.getCustomLogo.status = "idle";
      state.getCustomLogo.errorMessage = "";
      state.getCustomLogo.response = [];
      state.getSettings.status = "idle";
      state.getSettings.errorMessage = "";
      state.customLogo = "";
      state.workerData.firstName = "";
      state.workerData.lastName = "";
      state.workerData.contact = "";
      state.shiftsConfirmed = [];
      state.upcomingShifts = [];
      state.getConfirmedShifts.status = "idle";
      state.getConfirmedShifts.errorMessage = "";
      state.getConfirmedShifts.canLoadMore = true;
      state.getRegularShifts.status = "idle";
      state.getRegularShifts.errorMessage = "";
      state.getRegularShifts.canLoadMore = true;
      state.getUpcomingShifts.status = "idle";
      state.getUpcomingShifts.errorMessage = "";
      state.getUpcomingShifts.canLoadMore = true;
      localStorage.removeItem("worker-token");
      localStorage.removeItem("worker-refreshToken");
      localStorage.removeItem("worker-details");
      state.Refresh.status = "idle";
      state.Refresh.errorMessage = "";
    });
    builder.addCase(verifyPasscode.fulfilled, (state, action) => {
      const { firstName, lastName, id, token, refreshToken, WorkerContact } = action.payload;
      localStorage.setItem("worker-token", token);
      localStorage.setItem("worker-refreshToken", refreshToken);
      localStorage.setItem(
        "worker-details",
        JSON.stringify({ firstName, lastName, WorkerContact })
      );
      state.authenticated = true;
      state.workerData.firstName = firstName;
      state.workerData.lastName = lastName;
      state.workerData.contact = WorkerContact;
      state.workerId = id;
      state.verifyPasscode.status = "fulfilled passcode";
      state.verifyPasscode.errorMessage = "";
      state.verifyPasscode.isLocked = false;
      state.getPasscode.status = "fulfilled idle";
    });
    builder.addCase(verifyPasscode.pending, (state, action) => {
      state.verifyPasscode.status = "pending";
      state.authenticated = false;
      state.verifyPasscode.errorMessage = "";
      state.getPasscode.status = "fulfilled idle";
    });
    builder.addCase(verifyPasscode.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.verifyPasscode.status = "error";
      } else {
        state.verifyPasscode.status = "error passcode";
        const errorMessage = i18next.t("worker-slice_error-passcode-incorrect");
        state.verifyPasscode.errorMessage = action.payload
          ? action?.payload?.message
            ? action?.payload?.message
            : errorMessage
          : errorMessage;
        state.verifyPasscode.isLocked = action.payload
          ? action.payload.isLocked
            ? action.payload.isLocked
            : false
          : false;
        state.getPasscode.status = "fulfilled";
      }
    });
    builder.addCase(getConfirmedShifts.pending, (state, action) => {
      state.getConfirmedShifts.status = "pending";
      state.getConfirmedShifts.canLoadMore = true;
    });
    builder.addCase(getConfirmedShifts.fulfilled, (state, action) => {
      const endDateTime = (
        action.payload as unknown as {
          endDateTime: string;
        }
      ).endDateTime;
      const shifts = (
        action.payload as never as {
          shifts: Shift[];
          updating: boolean;
          endDateTime: string;
        }
      ).shifts;
      const updating = (
        action.payload as never as {
          shifts: Shift[];
          updating: boolean;
          endDateTime: string;
        }
      ).updating;
      let existingConfirmedShifts: Shift[] = [...state.shiftsConfirmed];
      let confirmedShifts: Shift[] = [];
      if (Array.isArray(shifts)) {
        // For full time worker normal confirmed shifts are overtime shifts
        // For a flex worker it is a regular shift
        shifts.forEach((obj) => {
          confirmedShifts.push({
            ...obj,
            isWorkerSkillExpired: Object.prototype.hasOwnProperty.call(
              obj,
              "shiftSkills"
            )
              ? obj.shiftSkills
                ? obj.shiftSkills.some((skill) => skill.isWorkerSkillExpired)
                : false
              : false,
            shiftType: isFullTimeWorker({ ...state.settings })
              ? OVERTIME
              : isPartTimeWorker({ ...state.settings })
              ? PART_TIME
              : "",
            status: obj.status ?? "",
          });
        });
      }
      // Sort new confirmed shifts
      confirmedShifts = confirmedShifts.sort((a: Shift, b: Shift) => {
        return (
          new Date(a.startDateTime).getTime() -
          new Date(b.startDateTime).getTime()
        );
      });
      // Append new confirmed shift
      existingConfirmedShifts =
        existingConfirmedShifts.concat(confirmedShifts);
      // Remove duplicate shifts
      existingConfirmedShifts = existingConfirmedShifts.filter(
        (v, i, a) => a.findIndex((v2) => v2.id === v.id) === i
      );
      // Sort based on start date
      existingConfirmedShifts = existingConfirmedShifts.sort(
        (a: Shift, b: Shift) => {
          return (
            new Date(a.startDateTime).getTime() -
            new Date(b.startDateTime).getTime()
          );
        }
      );
      // Update shifts confirmed state
      state.shiftsConfirmed = updating
        ? confirmedShifts
        : existingConfirmedShifts;
      state.getConfirmedShifts.canLoadMore = true;
      state.getConfirmedShifts.errorMessage = "";
      state.getConfirmedShifts.loadedTill = endDateTime;
      state.getConfirmedShifts.status = "fulfilled";
    });
    builder.addCase(getConfirmedShifts.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.getConfirmedShifts.status = "error";
      } else {
        const error = action.payload?.message;
        if (error === "No data found") {
          state.getConfirmedShifts.status = "error";
          state.getConfirmedShifts.errorMessage = i18next.t(
            "worker-slice_you-have-no-upcoming-shifts-scheduled"
          );
        } else {
          state.getConfirmedShifts.status = "error";
          state.getConfirmedShifts.errorMessage =
            error ?? i18next.t("worker-slice_error-occurred");
        }
      }
    });
    builder.addCase(getUpcomingShifts.pending, (state, action) => {
      state.getUpcomingShifts.status = "pending";
    });

    builder.addCase(getUpcomingShifts.fulfilled, (state, action) => {
      const endDateTime = (
        action.payload as unknown as {
          endDateTime: string;
        }
      ).endDateTime;
      const shifts = (
        action.payload as unknown as {
          shifts: Shift[];
          updating: boolean;
          endDateTime: string;
        }
      ).shifts;
      const updating = (
        action.payload as unknown as {
          shifts: Shift[];
          updating: boolean;
          endDateTime: string;
        }
      ).updating;
      let existingUpcomingShifts: Shift[] = [...state.upcomingShifts];
      let upcomingShifts: Shift[] = [];
      if (Array.isArray(shifts)) {
        // For full time worker normal confirmed shifts are overtime shifts
        // For a flex worker it is a regular shift
        shifts.forEach((obj) => {
          upcomingShifts.push({
            ...obj,
            isWorkerSkillExpired: Object.prototype.hasOwnProperty.call(
              obj,
              "shiftSkills"
            )
              ? obj.shiftSkills
                ? obj.shiftSkills.some((skill) => skill.isWorkerSkillExpired)
                : false
              : false,
            shiftType: isFullTimeWorker({ ...state.settings })
              ? OVERTIME
              : isPartTimeWorker({ ...state.settings })
              ? PART_TIME
              : "",
            status: obj.status ?? "",
          });
        });
      }
      // Sort based on start date
      upcomingShifts = upcomingShifts.sort((a: Shift, b: Shift) => {
        return (
          new Date(a.startDateTime).getTime() -
          new Date(b.startDateTime).getTime()
        );
      });
      // Append new shifts to existing one, overwrite in case it overlap.
      existingUpcomingShifts = overwriteShiftObjectsWithIds(
        existingUpcomingShifts,
        upcomingShifts
      );
      // Sort based on start date
      existingUpcomingShifts = existingUpcomingShifts.sort(
        (a: Shift, b: Shift) => {
          return (
            new Date(a.startDateTime).getTime() -
            new Date(b.startDateTime).getTime()
          );
        }
      );
      // Update shifts confirmed state replace with new data if updating
      state.upcomingShifts = updating
        ? upcomingShifts
        : existingUpcomingShifts;
      state.getUpcomingShifts.canLoadMore = true;
      state.getUpcomingShifts.loadedTill = endDateTime;
      state.getUpcomingShifts.errorMessage = "";
      state.getUpcomingShifts.status = "fulfilled";
    });
    builder.addCase(getUpcomingShifts.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.getUpcomingShifts.status = "error";
      } else {
        const error = action.payload?.message;
        if (error === "No data found") {
          state.getUpcomingShifts.status = "error";
          state.getUpcomingShifts.errorMessage = i18next.t(
            "worker-slice_no-shifts-available"
          );
        } else {
          state.getUpcomingShifts.status = "error";
          state.getUpcomingShifts.errorMessage =
            error ?? i18next.t("worker-slice_error-occurred");
        }
      }
    });
    builder.addCase(confirmShift.pending, (state) => {
      state.confirmShift.status = "pending";
      state.confirmShift.errorMessage = "";
      state.confirmShift.acceptanceWindowClosed = false;
    });
    builder.addCase(confirmShift.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.confirmShift.status = "error";
      } else {
        const data = action.payload ?? {};
        const settings = { ...state.settings };
        state.confirmShift.status = "error";
        state.confirmShift.shifts = Object.prototype.hasOwnProperty.call(
          data,
          "shifts"
        )
          ? data.shifts
          : [];
        const maxWorkHours = Object.prototype.hasOwnProperty.call(
          data,
          "maxWorkHours"
        )
          ? data.maxWorkHours
          : 0;
        const maxContinuousDays = Object.prototype.hasOwnProperty.call(data,"maxContinuousDays")
          ? data.maxContinuousDays
          : 0;
        const startDateTime = Object.prototype.hasOwnProperty.call(
          data,
          "startDateTime"
        )
          ? data.startDateTime
          : "";
        const endDateTime = Object.prototype.hasOwnProperty.call(
          data,
          "endDateTime"
        )
          ? data.endDateTime
          : "";
        if (startDateTime !== "" && endDateTime !== "" && maxWorkHours !== 0) {
          state.confirmShift.weeklyHoursLimitMessage =
            getWeeklyHoursLimitMessage(settings, startDateTime, endDateTime);
        } else if (startDateTime !== "" && endDateTime !== "" && maxContinuousDays !== 0) {
          state.confirmShift.continuousWorkDayLimitMessage =
            getContinuousWorkDayLimitMessage(
              maxContinuousDays
            );
        } else {
          state.confirmShift.weeklyHoursLimitMessage = "";
          state.confirmShift.continuousWorkDayLimitMessage = "";
        }
        // The server is the final word on the acceptance cutoff: a Worker can
        // be inside the dialog when the window shuts. Surface it in the same
        // error box rather than a toast, which is easy to miss.
        const isAcceptanceWindowClosed =
          data?.errorCode === ACCEPTANCE_WINDOW_CLOSED_ERROR;
        state.confirmShift.acceptanceWindowClosed = isAcceptanceWindowClosed;
        if (isAcceptanceWindowClosed) {
          state.confirmShift.weeklyHoursLimitMessage = "";
          state.confirmShift.continuousWorkDayLimitMessage = "";
          state.confirmShift.errorMessage = i18next.t(
            "signup-shift-dialog_acceptance-closed-generic"
          );
        } else {
          state.confirmShift.errorMessage =
            Object.prototype.hasOwnProperty.call(data, "message")
              ? data?.message
              : i18next.t("worker-slice_error-occurred");
        }
      }
    });
    builder.addCase(confirmShift.fulfilled, (state, action) => {
      const data = action.payload as unknown as {
        shiftId: number;
        status: string;
        checkConsecutiveShiftSignup: boolean;
        startDateTime: string;
        endDateTime: string;
        flexiStartDateTime?: string;
        flexiEndDateTime?: string;
      };

      let jobOrSkills = "";
      state.upcomingShifts.forEach((el: Shift) => {
        if (el.id === data.shiftId) {
          el.status = "A";
          jobOrSkills = el.jobName || (el.shiftSkills && el.shiftSkills[0]?.skillName) || "";
        }
      });

      state.confirmShift.status = "fulfilled";
      state.confirmShift.lastConfirmedShiftDetails = {
        status: data.status,
        startDateTime: data.flexiStartDateTime || data.startDateTime,
        endDateTime: data.flexiEndDateTime || data.endDateTime,
        jobOrSkills: jobOrSkills,
      };
      state.signupShiftDialog.isOpen = false;
      state.partialShiftSignUp.isOpen = false;
      state.flexiShiftSignUp.isOpen = false;
    });

    builder.addCase(viewShift.pending, (state) => {
      state.viewShift.status = "pending";
      state.viewShift.errorMessage = "";
    });
    builder.addCase(viewShift.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.viewShift.status = "error";
      } else {
        const data = action.payload ?? {};
        state.viewShift.status = "error";
        state.viewShift.errorMessage = Object.prototype.hasOwnProperty.call(
          data,
          "message"
        )
          ? data?.message
          : i18next.t("worker-slice_error-occurred");
      }
    });
    builder.addCase(viewShift.fulfilled, (state, action) => {
      state.viewShift.errorMessage = "";
      state.viewShift.status = "fulfilled";
      state.viewShift.viewedShifts = action.payload ?? [];
    });

    builder.addCase(markShiftsStatus.pending, (state) => {
      state.markShiftsStatus.status = "pending";
      state.markShiftsStatus.errorMessage = "";
    });
    builder.addCase(markShiftsStatus.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.markShiftsStatus.status = "error";
      } else {
        const data = action.payload ?? {};
        state.markShiftsStatus.status = "error";
        state.markShiftsStatus.errorMessage =
          Object.prototype.hasOwnProperty.call(data, "message")
            ? data?.message
            : i18next.t("worker-slice_error-occurred");
      }
    });
    builder.addCase(markShiftsStatus.fulfilled, (state, action) => {
      state.markShiftsStatus.errorMessage = "";
      state.markShiftsStatus.status = "fulfilled";
      state.markShiftsStatus.viewedShifts = action.payload ?? [];
    });

    builder.addCase(declineShift.pending, (state) => {
      state.declineShift.status = "pending";
    });
    builder.addCase(declineShift.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.declineShift.status = "error";
      } else {
        state.declineShift.status = "error";
        state.declineShift.errorMessage =
          action.payload?.message ?? i18next.t("worker-slice_error-occurred");
        state.declineShift.startDateTime = undefined;
        state.declineShift.endDateTime = undefined;
      }
    });
    builder.addCase(declineShift.fulfilled, (state, action) => {
      const shiftId: number = action.payload?.shiftId as unknown as number;

      state.upcomingShifts.forEach((el: Shift) => {
        if (el.id === shiftId) {
          el.status = "D";
        }
      });

      state.declineShift.status = "fulfilled";
      state.signupShiftDialog.isOpen = false;
      state.partialShiftSignUp.isOpen = false;
      state.flexiShiftSignUp.isOpen = false;
      state.declineShift.startDateTime = action.payload.startDateTime;
      state.declineShift.endDateTime = action.payload.endDateTime;
    });
    builder.addCase(cancelShift.pending, (state, action) => {
      state.cancelShift.status = "pending";
    });
    builder.addCase(cancelShift.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.cancelShift.status = "error";
      } else {
        state.cancelShift.status = "error";
        const errorMessage = i18next.t("worker-slice_error-occurred");
        state.cancelShift.errorMessage = action.payload?.message
          ? action.payload?.message
          : errorMessage;
        state.cancelShift.response = undefined;
        state.cancelShift.type = "C";
        state.cancelShift.startDateTime = undefined;
        state.cancelShift.endDateTime = undefined;
      }
    });
    builder.addCase(cancelShift.fulfilled, (state, action) => {
      try {
        const response = action.payload;
        if (Object.prototype.hasOwnProperty.call(response, "message")) {
          state.cancelShift.response = response;
          state.cancelShift.status = "error";
        } else {
          const shiftId = response.shiftId;
          const index = state.shiftsConfirmed.findIndex(
            (el) => el.id === shiftId
          );
          if (index > -1) {
            state.shiftsConfirmed[index].status = "C";
          }
          state.cancelShift.status = "fulfilled";
          state.cancelShift.type = response.status;
          state.cancelShift.startDateTime = response.startDateTime;
          state.cancelShift.endDateTime = response.endDateTime;
        }
      } catch (error) {
        Sentry.captureException(error);
        state.cancelShift.status = "error";
        state.cancelShift.errorMessage =
          action.payload ?? i18next.t("worker-slice_error-occurred");
        state.cancelShift.type = "C";
        state.cancelShift.response = undefined;
        state.cancelShift.startDateTime = undefined;
        state.cancelShift.endDateTime = undefined;
      }
    });
    // forceCancel
    builder.addCase(forceCancelShift.pending, (state) => {
      state.forceCancelShift.status = "pending";
    });
    builder.addCase(forceCancelShift.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.forceCancelShift.status = "error";
      } else {
        state.forceCancelShift.status = "error";
        const errorMessage = i18next.t("worker-slice_error-occurred");
        state.cancelShift.errorMessage = action.payload?.message
          ? action.payload?.message
          : errorMessage;
      }
    });
    builder.addCase(forceCancelShift.fulfilled, (state, action) => {
      try {
        const shiftId = action.payload.shiftId;
        const index = state.shiftsConfirmed.findIndex(
          (el) => el.id === shiftId
        );
        if (index > -1) {
          state.shiftsConfirmed[index].status = "C";
        }
        state.forceCancelShift.status = "idle";
        state.cancelShift.status = "fulfilled";
        state.cancelShift.type = "C";
        state.cancelShift.startDateTime = action.payload.startDateTime;
        state.cancelShift.endDateTime = action.payload.endDateTime;
      } catch (error) {
        Sentry.captureException(error);
        state.cancelShift.status = "error";
        state.cancelShift.errorMessage = i18next.t(
          "worker-slice_error-occurred"
        );
        state.cancelShift.type = "C";
        state.cancelShift.response = undefined;
        state.cancelShift.startDateTime = undefined;
        state.cancelShift.endDateTime = undefined;
      }
    });
    //  -- forceCancel end
    builder.addCase(getSkillLevels.pending, (state) => {
      state.getSkillLevels.status = "pending";
    });
    builder.addCase(getSkillLevels.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.getSkillLevels.status = "error";
      } else {
        state.getSkillLevels.status = "error";
        const errorMessage = i18next.t("worker-slice_error-occurred");
        state.getSkillLevels.errorMessage = action.payload?.message
          ? action.payload?.message
          : errorMessage;
      }
    });
    builder.addCase(getSkillLevels.fulfilled, (state, action) => {
      const data = action.payload ?? [];
      let skillLevels = [];
      if (Object.prototype.hasOwnProperty.call(data, "level")) {
        skillLevels = data;
      } else if (data.length > 0) {
        // Getting companies array instead
        let selectedCompany = data[0];
        const fullTimeCompanies = data.filter(
          (company: { workerTypeName: string }) =>
            company.workerTypeName === "FullTime"
        );
        if (fullTimeCompanies) {
          if (fullTimeCompanies.length > 0) {
            selectedCompany = fullTimeCompanies[0];
          }
        }
        skillLevels = selectedCompany.skillLevels ?? [];
      }
      if (skillLevels.length > 0) {
        skillLevels = skillLevels.map((el: { id: number }) => ({
          ...el,
          levelId: el.id,
        }));
      }
      state.skillLevels = skillLevels;
      state.getSkillLevels.status = "fulfilled";
      state.getSkillLevels.errorMessage = "";
    });
    builder.addCase(getProfile.pending, (state) => {
      state.workerProfile.status = "pending";
    });
    builder.addCase(getProfile.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.workerProfile.status = "error";
      } else {
        state.workerProfile.status = "error";
        const errorMessage = i18next.t("worker-slice_error-occurred");
        state.workerProfile.errorMessage = action.payload?.message
          ? action.payload?.message
          : errorMessage;
      }
    });
    builder.addCase(getProfile.fulfilled, (state, action) => {
      state.workerProfile.data = action.payload;
      state.workerProfile.status = "fulfilled";
      if (action.payload) {
        const contact = action.payload.mobilePhoneNumber || action.payload.email || "";
        const firstName = action.payload.firstName || state.workerData.firstName;
        const lastName = action.payload.lastName || state.workerData.lastName;

        if (contact) {
          state.workerData.contact = contact;
        }
        state.workerData.firstName = firstName;
        state.workerData.lastName = lastName;

        const details = JSON.parse(localStorage.getItem("worker-details") || "{}");
        localStorage.setItem(
          "worker-details",
          JSON.stringify({ ...details, firstName, lastName, WorkerContact: contact || details.WorkerContact })
        );
      }
    });
    builder.addCase(getProfileSkills.pending, (state) => {
      state.profileSkills.status = "pending";
    });
    builder.addCase(getProfileSkills.rejected, (state, action: any) => {
      state.profileSkills.status = "error";
      state.profileSkills.errorMessage = action.payload?.data?.message
        ? action.payload?.data?.message
        : i18next.t("worker-slice_error-occurred");
    });
    builder.addCase(getProfileSkills.fulfilled, (state, action: any) => {
      state.profileSkills.status = "fulfilled";
      const jobs = Array.isArray(action.payload?.jobs)
        ? [...action.payload.jobs].sort((a: any, b: any) =>
            (a?.jobName ?? "").localeCompare(b?.jobName ?? "")
          )
        : [];
      state.profileSkills.jobs = jobs;
      state.profileSkills.otherSkills = Array.isArray(
        action.payload?.otherSkills
      )
        ? action.payload.otherSkills
        : [];
    });
    builder.addCase(getWorkHistory.pending, (state) => {
      state.workHistory.status = "pending";
    });
    builder.addCase(getWorkHistory.rejected, (state, action: any) => {
      state.workHistory.status = "error";
      state.workHistory.errorMessage = action.payload?.data?.message
        ? action.payload?.data?.message
        : i18next.t("worker-slice_error-occurred");
    });
    builder.addCase(getWorkHistory.fulfilled, (state, action: any) => {
      state.workHistory.status = "fulfilled";
      state.workHistory.attendedShifts = Array.isArray(
        action.payload?.attendedShifts
      )
        ? action.payload.attendedShifts
        : [];
      state.workHistory.otMinutes = action.payload?.otMinutes ?? 0;
    });
    builder.addCase(createSwapRequest.pending, (state) => {
      state.createSwapRequest.status = "pending";
      state.createSwapRequest.errorMessage = "";
    });
    builder.addCase(createSwapRequest.rejected, (state, action: any) => {
      state.createSwapRequest.status = "error";
      state.createSwapRequest.errorMessage = action.payload?.message
        ? action.payload.message
        : i18next.t("offer-shift_shift-swap-error");
    });
    builder.addCase(createSwapRequest.fulfilled, (state, action: any) => {
      state.createSwapRequest.status = "fulfilled";
      state.createSwapRequest.errorMessage = "";
      state.createSwapRequest.response = action.payload?.response;
    });
    builder.addCase(getNotifications.pending, (state) => {
      state.getNotifications.status = "pending";
      state.getNotifications.errorMessage = "";
    });
    builder.addCase(getNotifications.rejected, (state, action: any) => {
      state.getNotifications.status = "error";
      state.getNotifications.errorMessage =
        action.payload?.message ?? i18next.t("worker-slice_error-occurred");
    });
    builder.addCase(getNotifications.fulfilled, (state, action: any) => {
      state.getNotifications.status = "fulfilled";
      state.getNotifications.errorMessage = "";
      const incoming = Array.isArray(action.payload) ? action.payload : [];
      // Merge + de-dupe by id (first wins), newest first.
      const merged = [...state.getNotifications.notifications];
      const seen = new Set(merged.map((n: any) => n.id));
      incoming.forEach((n: any) => {
        if (!seen.has(n.id)) {
          merged.push(n);
          seen.add(n.id);
        }
      });
      merged.sort((a: any, b: any) => b.id - a.id);
      state.getNotifications.notifications = merged;
    });
    builder.addCase(getRegularShifts.pending, (state) => {
      state.getRegularShifts.status = "pending";
    });
    builder.addCase(getRegularShifts.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.getRegularShifts.status = "error";
      } else {
        state.getRegularShifts.status = "error";
        state.getRegularShifts.errorMessage =
          action.payload?.message ?? i18next.t("worker-slice_error-occurred");
      }
    });
    builder.addCase(getRegularShifts.fulfilled, (state, action) => {
      const endDateTime = (
        action.payload as unknown as {
          endDateTime: string;
        }
      ).endDateTime;
      const data =
        (
          action.payload as unknown as {
            shifts: Shift[];
            updating: boolean;
            endDateTime: string;
          }
        ).shifts ?? [];
      const updating = (
        action.payload as unknown as {
          shifts: Shift[];
          updating: boolean;
          endDateTime: string;
        }
      ).updating;
      let regularShifts: Shift[] = [];
      let existingRegularShifts: Shift[] = [...state.regularShifts];
      if (Array.isArray(data)) {
        data.forEach((obj) => {
          regularShifts.push({
            ...obj,
            shiftType: REGULAR,
            status: "",
          });
        });
      }
      // Sort regular shifts
      regularShifts = regularShifts.sort((a, b) => {
        return (
          new Date(a.startDateTime).getTime() -
          new Date(b.startDateTime).getTime()
        );
      });
      // Append new regular shift
      existingRegularShifts = existingRegularShifts.concat(regularShifts);
      // Remove duplicate shifts
      existingRegularShifts = existingRegularShifts.filter(
        (v, i, a) => a.findIndex((v2) => v2.ftShiftId === v.ftShiftId) === i
      );
      // Sort based on start date
      existingRegularShifts = existingRegularShifts.sort((a, b) => {
        return (
          new Date(a.startDateTime).getTime() -
          new Date(b.startDateTime).getTime()
        );
      });
      // Update regular shifts state
      state.regularShifts = updating ? regularShifts : existingRegularShifts;
      state.getRegularShifts.canLoadMore = true;
      state.getRegularShifts.errorMessage = "";
      state.getRegularShifts.loadedTill = endDateTime;
      state.getRegularShifts.status = "fulfilled";
    });
    builder.addCase(getFTSchedulingStatuses.pending, (state) => {
      state.ftSchedulingStatus.status = "pending";
    });
    builder.addCase(getFTSchedulingStatuses.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.ftSchedulingStatus.status = "error";
      } else {
        state.ftSchedulingStatus.status = "error";
        state.ftSchedulingStatus.errorMessage =
          action.payload?.message ?? i18next.t("worker-slice_error-occurred");
      }
    });
    builder.addCase(getFTSchedulingStatuses.fulfilled, (state, action) => {
      state.ftSchedulingStatus.status = "fulfilled";
      state.ftSchedulingStatus.errorMessage = "";
      state.ftSchedulingStatus.schedulingStatus = action.payload ?? []; // Do not use directly for logo
    });

    builder.addCase(getCustomLogo.pending, (state) => {
      state.getCustomLogo.status = "pending";
    });
    builder.addCase(getCustomLogo.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.getCustomLogo.status = "error";
      } else {
        state.getCustomLogo.status = "error";
        state.getCustomLogo.errorMessage =
          action.payload?.message ?? i18next.t("worker-slice_error-occurred");
      }
    });
    builder.addCase(getCustomLogo.fulfilled, (state, action) => {
      state.getCustomLogo.status = "fulfilled";
      state.getCustomLogo.errorMessage = "";
      state.getCustomLogo.response = action.payload ?? []; // Do not use directly for logo
    });
    builder.addCase(fetchCancelShiftReasons.pending, (state) => {
      state.getCancelShiftReasons.status = "pending";
    });
    builder.addCase(fetchCancelShiftReasons.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.getCancelShiftReasons.status = "error";
      } else {
        state.getCancelShiftReasons.status = "error";
        state.getCancelShiftReasons.errorMessage =
          action.payload?.message ?? i18next.t("worker-slice_error-occurred");
      }
    });
    builder.addCase(fetchCancelShiftReasons.fulfilled, (state, action) => {
      state.getCancelShiftReasons.status = "fulfilled";
      state.getCancelShiftReasons.errorMessage = "";
      state.cancelShiftReasons = action.payload ?? [];
    });
    builder.addCase(fetchSpotsLeft.pending, (state) => {
      state.getSpotsLeft.status = "pending";
    });
    builder.addCase(fetchSpotsLeft.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.getSpotsLeft.status = "error";
      } else {
        state.getSpotsLeft.status = "error";
        state.getSpotsLeft.errorMessage =
          action.payload?.message ?? i18next.t("worker-slice_error-occurred");
      }
    });
    builder.addCase(fetchSpotsLeft.fulfilled, (state, action) => {
      state.getSpotsLeft.status = "fulfilled";
      state.getSpotsLeft.errorMessage = "";
      state.upcomingShifts = state.upcomingShifts.map((shift) => {
        const spotsLeftShift = action.payload.find(
          (el: {
            shiftId: number;
            spotsLeft: number;
            slotsAvailability?: any;
            availableFlexiWindows?: any[];
            minFlexiSignupMinutes?: number;
            onlyCustomWindowAvailable?: boolean;
          }) => parseInt(el.shiftId.toString()) === parseInt(shift.id.toString())
        );
        if (spotsLeftShift) {
          shift.spotsLeft = spotsLeftShift.spotsLeft ?? 1;
          shift.onlyCustomWindowAvailable = spotsLeftShift.onlyCustomWindowAvailable ?? false;
          shift.slotsAvailability =
            spotsLeftShift.slotsAvailability ?? undefined;
          if (spotsLeftShift.availableFlexiWindows) {
            shift.availableFlexiWindows = spotsLeftShift.availableFlexiWindows;
          }
          if (spotsLeftShift.minFlexiSignupMinutes) {
            shift.minFlexiSignupMinutes = spotsLeftShift.minFlexiSignupMinutes;
          }
        }
        return shift;
      });
    });
    builder.addCase(getSettings.pending, (state) => {
      state.getSettings.status = "pending";
    });
    builder.addCase(getSettings.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        // Clear `pending` here too: the thunk's `condition` gate would
        // otherwise treat a rate-limited call as permanently in flight and
        // block every later settings fetch for the session.
        state.getSettings.status = "idle";
        state.getSettings.errorMessage = "";
        state.workerRateLimitError.isErrorOccurred = true;
      } else {
        // Logout if setting api failed
        state.authenticated = false;
        state.getPasscode.status = "idle";
        state.getPasscode.errorMessage = "";
        state.getPasscode.errorCode = "";
        state.verifyPasscode.status = "idle";
        state.verifyPasscode.errorMessage = "";
        state.verifyPasscode.isLocked = false;
        state.getCustomLogo.status = "idle";
        state.getCustomLogo.errorMessage = "";
        state.getSettings.status = "idle";
        state.getSettings.errorMessage = "";
        state.getCustomLogo.response = [];
        state.getSettings.status = "idle";
        state.getSettings.errorMessage = "";
        state.customLogo = "";
        state.shiftsConfirmed = [];
        state.upcomingShifts = [];
        state.getConfirmedShifts.status = "idle";
        state.getConfirmedShifts.errorMessage = "";
        state.getConfirmedShifts.canLoadMore = true;
        state.getRegularShifts.status = "idle";
        state.getRegularShifts.errorMessage = "";
        state.getRegularShifts.canLoadMore = true;
        state.getUpcomingShifts.status = "idle";
        state.getUpcomingShifts.errorMessage = "";
        state.getUpcomingShifts.canLoadMore = true;
        localStorage.removeItem("worker-token");
        localStorage.removeItem("worker-refreshToken");
        localStorage.removeItem("worker-details");
        state.Refresh.status = "idle";
        state.Refresh.errorMessage = "";
      }
    });

    builder.addCase(getAcknowledgements.pending, (state) => {
      state.getAcknowledgements.status = "pending";
      state.getAcknowledgements.errorMessage = "";
    });
    builder.addCase(getAcknowledgements.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.getAcknowledgements.status = "error";
      } else {
        state.getAcknowledgements.status = "error";
        state.getAcknowledgements.errorMessage = i18next.t(
          "worker-slice_error-unexpected-response"
        );
      }
    });
    builder.addCase(getAcknowledgements.fulfilled, (state, action) => {
      try {
        state.getAcknowledgements.status = "fulfilled";
        state.getAcknowledgements.errorMessage = "";
        /* Constants info
                    p : Present
                    a : Absent
                    t : Tardy
                    e : Excused Absent
                    x : Excused Tardy
                    * */
        const getAcknowledgementsResponseArray = Array.isArray(action.payload)
          ? action.payload
          : [];
        const allAcknowledgements: { attendStatus: string }[] = [];
        getAcknowledgementsResponseArray.forEach(
          (ack: { attendStatus: any }) => {
            switch (ack.attendStatus) {
              case "p":
                allAcknowledgements.push({ ...ack, attendStatus: PRESENT });
                break;
              case "a":
                allAcknowledgements.push({ ...ack, attendStatus: ABSENT });
                break;
              case "t":
                allAcknowledgements.push({ ...ack, attendStatus: TARDY });
                break;
              case "e":
                allAcknowledgements.push({
                  ...ack,
                  attendStatus: EXCUSED_ABSENT,
                });
                break;
              case "x":
                allAcknowledgements.push({
                  ...ack,
                  attendStatus: EXCUSED_TARDY,
                });
                break;
              default:
                break;
            }
          }
        );
        state.acknowledgements = allAcknowledgements ?? [];
      } catch (e) {
        // Do nothing
        state.getAcknowledgements.status = "error";
        state.getAcknowledgements.errorMessage = i18next.t(
          "worker-slice_problem-parsing-acknowledgements"
        );
      }
    });

    builder.addCase(getSettings.fulfilled, (state: any, action: any) => {
      state.getSettings.status = "fulfilled";
      state.getSettings.errorMessage = "";
      state.settings.smsNotifications = action.payload.smsNotifications;
      state.settings.smsNotificationsId = action.payload.smsNotificationsId;
      state.settings.showSmsToggle = action.payload.showSmsToggle;
      state.settings.whatsappNotifications =
        action.payload.whatsappNotifications;
      state.settings.whatsappNotificationsId =
        action.payload.whatsappNotificationsId;
      state.settings.companyAnnouncements =
        action.payload.companyAnnouncements;
      state.settings.companyAnnouncementsId =
        action.payload.companyAnnouncementsId;
      state.settings.shiftUpdates = action.payload.shiftUpdates;
      state.settings.shiftUpdatesId = action.payload.shiftUpdatesId;
      state.settings.attendanceUpdates = action.payload.attendanceUpdates;
      state.settings.attendanceUpdatesId = action.payload.attendanceUpdatesId;
      state.settings.profileUpdates = action.payload.profileUpdates;
      state.settings.profileUpdatesId = action.payload.profileUpdatesId;
      state.settings.quietDays = action.payload.quietDays;
      state.settings.quietDaysId = action.payload.quietDaysId;
      state.settings.smsDailyShiftReminders =
        action.payload.smsDailyShiftReminders;
      state.settings.smsDailyShiftRemindersId =
        action.payload.smsDailyShiftRemindersId;
      state.settings.displayAvailability = action.payload.displayAvailability;
      state.settings.displayAvailabilityId =
        action.payload.displayAvailabilityId;
      state.settings.showScore = action.payload.showScore;
      state.settings.showScoreId = action.payload.showScoreId;
      state.settings.showSkill = action.payload.showSkill;
      state.settings.showSkillId = action.payload.showSkillId;
      state.settings.initialized = action.payload.initialized;
      state.settings.autoAccept = action.payload.autoAccept;
      state.settings.autoAcceptId = action.payload.autoAcceptId;
      state.settings.dailyMaxHours = action.payload.dailyMaxHours;
      state.settings.dailyMaxHoursId = action.payload.dailyMaxHoursId;
      state.settings.workerAttendanceAcknowledgement =
        action.payload.workerAttendanceAcknowledgement;
      state.settings.workerAttendanceAcknowledgementId =
        action.payload.workerAttendanceAcknowledgementId;
      state.settings.showShiftAutoAccept = action.payload.showShiftAutoAccept;
      state.settings.showShiftAutoAcceptId =
        action.payload.showShiftAutoAcceptId;
      state.settings.showWhatsAppToggle = action.payload.showWhatsAppToggle;
      state.settings.fullTimeShiftSwapCancelEnabled =
        action.payload.fullTimeShiftSwapCancelEnabled;
      state.settings.timeOffEnabled = action.payload.timeOffEnabled;
      state.settings.workerProfileInfo = action.payload.workerProfileInfo;
      state.settings.workerProfileInfoId = action.payload.workerProfileInfoId;
      state.workerType = isFullTimeWorker(action.payload)
        ? FULL_TIME_WORKER
        : isPartTimeWorker(action.payload)
        ? PART_TIME_WORKER
        : FLEX_WORKER;
      state.settings.companyId = action.payload.companyId;
      state.settings.customizedBrandingEnabled =
        action.payload.customizedBrandingEnabled;
      state.settings.languagePreferenceId = action.payload.languagePreferenceId;
      state.settings.languagePreference = action.payload.languagePreference;
      state.settings.workerSubTypeEnabled = action.payload.workerSubTypeEnabled;
      state.settings.fullTimeSchedulingEnabled = action.payload.fullTimeSchedulingEnabled;
      state.settings.workerSetPrefEnabled = action.payload.workerSetPrefEnabled;
      state.settings.vakaySSOLinkEnabled = action.payload.vakaySSOLinkEnabled;
      state.settings.workerEvaluationEnabled =
        action.payload.workerEvaluationEnabled;
      const selectedCompany = getSelectedCompany(action.payload);
      if (
        Object.prototype.hasOwnProperty.call(selectedCompany, "maxWorkHours") &&
        Object.prototype.hasOwnProperty.call(
          selectedCompany,
          "remainingWorkHours"
        ) &&
        Object.prototype.hasOwnProperty.call(
          selectedCompany,
          "workerTypeName"
        ) &&
        Object.prototype.hasOwnProperty.call(selectedCompany, "workerTypeText")
      ) {
        state.settings.maxWorkHours = selectedCompany.maxWorkHours;
        state.settings.remainingWorkHours = selectedCompany.remainingWorkHours;
        state.settings.workerTypeText = selectedCompany.workerTypeText;
        state.settings.workerTypeName = selectedCompany.workerTypeName;
      }
      if (Object.prototype.hasOwnProperty.call(selectedCompany, "utilityWorker")) {
        state.settings.utilityWorker = selectedCompany.utilityWorker;
      }
      if (
        Object.prototype.hasOwnProperty.call(
          selectedCompany,
          "showWeeklyHoursLimitNoticeOn"
        )
      ) {
        state.getSettings.showWeeklyHoursLimitNoticeOn =
          selectedCompany.showWeeklyHoursLimitNoticeOn;
      }

      // Heap additional information added
      try {
        if (
          action.payload &&
          (window as any).heap &&
          typeof (window as any).heap.identify === "function"
        ) {
          (window as any).heap.identify(selectedCompany.workerId);
          if (
            Object.prototype.hasOwnProperty.call(selectedCompany, "workerId") &&
            Object.prototype.hasOwnProperty.call(
              selectedCompany,
              "companyName"
            ) &&
            Object.prototype.hasOwnProperty.call(
              selectedCompany,
              "companyId"
            ) &&
            Object.prototype.hasOwnProperty.call(
              selectedCompany,
              "workerTypeText"
            )
          ) {
            (window as any).heap.addUserProperties({
              companyName: selectedCompany.companyName,
              companyId: selectedCompany.companyId,
              workerType: selectedCompany.workerTypeText,
              fistName: state.workerData.firstName,
              lastName: state.workerData.lastName,
            });
          }
        }
      } catch (e) {
        console.log("Heap failure", e);
      }
    });

    builder.addCase(setSmsSettings.pending, (state: any, action: any) => {
      state.setSmsSettings.status = "pending";
      state.setSmsSettings.errorMessage = "";
    });
    builder.addCase(setSmsSettings.rejected, (state: any, action: any) => {
      if (action?.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.setSmsSettings.status = "error";
      } else {
        state.setSmsSettings.status = "error";
        state.setSmsSettings.errorMessage =
          action.payload?.message ?? "An error occurred";
        try {
          switch (action.payload.key) {
            case SETTINGS_LANGUAGE_PREFERENCE:
              state.settings.languagePreference = action.payload.value;
              break;
            default: {
              // Revert the optimistic toggle to its original value.
              const field = SETTING_KEY_TO_FIELD[action.payload.key];
              if (field) {
                state.settings[field] = action.payload.value;
              }
              break;
            }
          }
        } catch (e) {
          // Do nothing
        }
      }
    });
    builder.addCase(setSmsSettings.fulfilled, (state: any, action: any) => {
      state.setSmsSettings.status = "fulfilled";
      state.setSmsSettings.errorMessage = "";
      switch (action.payload.key) {
        case SETTINGS_LANGUAGE_PREFERENCE:
          state.settings.languagePreference = action.payload.value;
          break;
        default: {
          const field = SETTING_KEY_TO_FIELD[action.payload.key];
          if (field) {
            state.settings[field] = action.payload.value;
          }
          break;
        }
      }
    });
    builder.addCase(fetchLeaveTypes.pending, (state: any) => {
      state.getLeaveTypes.status = "pending";
      state.getLeaveTypes.errorMessage = "";
    });
    builder.addCase(fetchLeaveTypes.rejected, (state: any, action: any) => {
      if (!action.payload?.canceled) {
        if (action.payload?.status === 429) {
          state.workerRateLimitError.isErrorOccurred = true;
          state.getLeaveTypes.status = "error";
        } else {
          state.getLeaveTypes.status = "error";
          state.getLeaveTypes.errorMessage =
            action.payload?.message ?? i18n.t("worker-slice_error-occurred");
          state.getLeaveTypes.leaveTypes = [];
        }
      }
    });
    builder.addCase(fetchLeaveTypes.fulfilled, (state: any, action: any) => {
      state.getLeaveTypes.status = "fulfilled";
      state.getLeaveTypes.errorMessage = "";
      state.getLeaveTypes.leaveTypes = action.payload ?? [];
    });
    builder.addCase(fetchRequestTypes.pending, state => {
      state.getRequestTypes.status = "pending";
      state.getRequestTypes.errorMessage = "";
    });
    builder.addCase(fetchRequestTypes.rejected, (state:any, action:any) => {
      if (!action.payload?.canceled) {
        if (action.payload?.status === 429) {
          state.workerRateLimitError.isErrorOccurred = true;
          state.getRequestTypes.status = "error";
        } else {
          state.getRequestTypes.status = "error";
          state.getRequestTypes.errorMessage =
            action.payload?.message ?? i18n.t("worker-slice_error-occurred");
          state.getRequestTypes.requestTypes = [];
        }
      }
    });
    builder.addCase(fetchRequestTypes.fulfilled, (state, action) => {
      state.getRequestTypes.status = "fulfilled";
      state.getRequestTypes.errorMessage = "";
      state.getRequestTypes.requestTypes = action.payload ?? [];
    });
    builder.addCase(submitLeaveRequest.fulfilled, state => {
      state.submitLeaveRequest.status = "fulfilled";
      state.submitLeaveRequest.errorMessage = "";
    });

    builder.addCase(submitLeaveRequest.rejected, (state:any, action:any) => {
      if (!action.payload?.canceled) {
        if (action.payload?.status === 429) {
          state.workerRateLimitError.isErrorOccurred = true;
          state.submitLeaveRequest.status = "error";
        } else {
          state.submitLeaveRequest.status = "error";
          state.submitLeaveRequest.errorMessage = action.payload?.message;
          state.submitLeaveRequest.ftShiftId = "";
          state.submitLeaveRequest.startDateTime = undefined;
          state.submitLeaveRequest.endDateTime = undefined;
        }
      }
    });

    builder.addCase(submitLeaveRequest.pending, state => {
      state.submitLeaveRequest.status = "pending";
      state.submitLeaveRequest.errorMessage = "";
    });
    builder.addCase(submitLeaveRequestBatch.pending, (state) => {
      state.submitLeaveRequestBatch.status = "pending";
      state.submitLeaveRequestBatch.errorMessage = "";
    });
    builder.addCase(
      submitLeaveRequestBatch.fulfilled,
      (state: any, action: any) => {
        state.submitLeaveRequestBatch.status = "fulfilled";
        state.submitLeaveRequestBatch.errorMessage = "";
        state.submitLeaveRequestBatch.createdIds =
          action.payload?.createdIds ?? [];
        state.submitLeaveRequestBatch.shifts = action.payload?.shifts ?? [];
      }
    );
    builder.addCase(
      submitLeaveRequestBatch.rejected,
      (state: any, action: any) => {
        if (!action.payload?.canceled) {
          if (action.payload?.status === 429) {
            state.workerRateLimitError.isErrorOccurred = true;
            state.submitLeaveRequestBatch.status = "error";
          } else {
            state.submitLeaveRequestBatch.status = "error";
            state.submitLeaveRequestBatch.errorMessage = action.payload?.message;
          }
        }
      }
    );
    builder.addCase(fetchLeaveRequests.pending, (state) => {
      state.getLeaveRequests.status = "pending";
      state.getLeaveRequests.errorMessage = "";
    });
    builder.addCase(fetchLeaveRequests.rejected, (state: any, action: any) => {
      if (!action.payload?.canceled) {
        if (action.payload?.status === 429) {
          state.workerRateLimitError.isErrorOccurred = true;
          state.getLeaveRequests.status = "error";
        } else {
          state.getLeaveRequests.status = "error";
          state.getLeaveRequests.errorMessage =
            action.payload?.message ?? i18n.t("worker-slice_error-occurred");
          state.getLeaveRequests.leaveRequests = [];
        }
      }
    });
    builder.addCase(fetchLeaveRequests.fulfilled, (state, action) => {
      state.getLeaveRequests.status = "fulfilled";
      state.getLeaveRequests.errorMessage = "";
      state.submitLeaveRequest.ftShiftId = "";
      state.submitLeaveRequest.startDateTime = undefined;
      state.submitLeaveRequest.endDateTime = undefined;
      let newLeaveRequests = action.payload ?? [];
      newLeaveRequests = newLeaveRequests.map((el: any) => ({
        ...el,
        id: el.ftShiftId,
      }));
      let existingLeaveRequests = [...state.getLeaveRequests.leaveRequests];
      // Append new upcoming shift
      existingLeaveRequests = overwriteObjectsWithIds(
        existingLeaveRequests,
        newLeaveRequests
      );

      state.getLeaveRequests.leaveRequests = existingLeaveRequests ?? [];
    });
    builder.addCase(withdrawLeaveRequest.pending, state => {
      state.withdrawLeaveRequest.status = "pending";
      state.withdrawLeaveRequest.errorMessage = "";
    });
    builder.addCase(withdrawLeaveRequest.rejected, (state:any, action:any) => {
      if (!action.payload?.canceled) {
        if (action.payload?.status === 429) {
          state.workerRateLimitError.isErrorOccurred = true;
          state.withdrawLeaveRequest.status = "error";
        } else {
          state.withdrawLeaveRequest.status = "error";
          state.withdrawLeaveRequest.errorMessage =
            action.payload?.message ?? "An error occurred";
        }
      }
    });
    builder.addCase(withdrawLeaveRequest.fulfilled, (state:any, action:any) => {
      state.withdrawLeaveRequest.status = "fulfilled";
      state.withdrawLeaveRequest.errorMessage = "";
      const ftShiftId = state.withdrawLeaveRequest.ftShiftId;
      const index = state.getLeaveRequests.leaveRequests.findIndex(
        (el:any) => el.id === ftShiftId,
      );
      if (index > -1) {
        state.getLeaveRequests.leaveRequests.splice(index, 1);
      }
    });
    builder.addCase(acknowledgeShift.pending, (state) => {
      state.acknowledgeShift.status = "pending";
    });
    builder.addCase(acknowledgeShift.rejected, (state, action: any) => {
      if (action?.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.acknowledgeShift.status = "error";
      } else {
        state.acknowledgeShift.status = "error";
        state.acknowledgeShift.errorMessage =
          action.payload?.message ?? i18next.t("worker-slice_error-occurred");
      }
    });
    builder.addCase(acknowledgeShift.fulfilled, (state, action) => {
      state.acknowledgeShift.status = "fulfilled";
      state.acknowledgeShift.errorMessage = "";
      state.acknowledgeShift.data = action.payload;
      // remove the acknowledged shift from the list
      state.acknowledgements = state.acknowledgements.filter(
        (ack: any) => ack.id !== action.payload.id
      );
    });
    builder.addCase(getWorkerPreferences.pending, state => {
      state.getWorkerPreferences.status = "pending";
      state.getWorkerPreferences.errorMessage = "";
    });
    builder.addCase(getWorkerPreferences.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.getWorkerPreferences.status = "error";
      } else {
        state.getWorkerPreferences.status = "error";
        state.getWorkerPreferences.errorMessage =
          action.payload?.message ?? i18n.t("worker-slice_error-occurred");
      }
    });
    builder.addCase(getWorkerPreferences.fulfilled, (state, action) => {
      state.getWorkerPreferences.status = "fulfilled";
      state.getWorkerPreferences.errorMessage = "";
      state.getWorkerPreferences.utilityWorker = action.payload?.isUtilityWorker ?? false;
      state.jobPreferences = action.payload?.jobPreferences?.preferences ?? [];
      state.shiftPreferences = action.payload?.shiftPreferences?.preferences ?? [];
      state.primaryJobName = action.payload?.jobPreferences?.primaryPreferenceName ?? "-";
      state.yourShift = action.payload?.shiftPreferences?.primaryPreferenceName ?? "-";
    });
    builder.addCase(saveWorkerPreferences.pending, state => {
      state.saveWorkerPreferences.status = "pending";
      state.saveWorkerPreferences.errorMessage = "";
    });
    builder.addCase(saveWorkerPreferences.rejected, (state, action: any) => {
      if (action.payload?.status === 429) {
        state.workerRateLimitError.isErrorOccurred = true;
        state.saveWorkerPreferences.status = "error";
      } else {
        state.saveWorkerPreferences.status = "error";
        state.saveWorkerPreferences.errorMessage =
          action.payload?.message ?? i18n.t("worker-slice_error-occurred");
      }
    });
    builder.addCase(saveWorkerPreferences.fulfilled, (state, action) => {
      state.saveWorkerPreferences.status = "fulfilled";
      state.saveWorkerPreferences.errorMessage = "";
    });
  },
});

export const {
  setCustomLogo,
  setTabToViewShifts,
  setWorkerPreferencePageOpen,
  setIsCancelShiftPageOpen,
  setIsAcknowledgementPageOpen,
  setTabToShiftSignup,
  setTab,
  resetCancelShift,
  resetConfirmShift,
  resetUpcomingShifts,
  resetGetRegularShifts,
  resetGetSettings,
  openSignupShiftDialog,
  closeSignupShiftDialog,
  setWorkerId,
  setWorker,
  closeViewShiftDialog,
  closeViewRegularShiftPage,
  openViewShiftDialog,
  openViewRegularShiftPage,
  logout,
  resetAcknowledgements,
  resetAcknowledgeShift,
  setWorkerRateLimitError,
  resetWorkerRateLimitError,
  openPartialShiftSignup,
  closePartialShiftSignup,
  openFlexiShiftSignup,
  closeFlexiShiftSignup,
  openLeaveRequestPage,
  closeLeaveRequestPage,
  openApplyTimeOff,
  closeApplyTimeOff,
  openOfferShiftPage,
  closeOfferShiftPage,
  resetCreateSwapRequest,
  setSettingsManually,
  resetSubmitLeaveRequestBatch,
  resetVerifyPasscode,
  resetSubmitLeaveRequest,
  resetPreSubmitLeaveRequest,
  setWidthdrawLeaveRequestFtShiftId,
  resetWithdrawLeaveRequest,
  presetSubmitLeaveRequest,
  resetSaveWorkerPreferences,
  resetGetWorkerPreferences,
  resetDeclineShift,
  resetConfirmShiftsLoadedTill,
  resetUpcomingShiftsLoadedTill,
  resetRegularShiftsLoadedTill,
  resetAssertVakaySSO,
} = WebWorkerSlice.actions;

export default WebWorkerSlice.reducer;